<template>

  <head>
    <meta charset="utf-8" />
    <title>County Revenue System | Client Database</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <link rel="shortcut icon" href="assets/images/favicon.svg">
    <link href="assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet" type="text/css" />
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/custom.css" rel="stylesheet" type="text/css" />
    <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
  </head>

  <body data-sidebar="dark">
  <div id="layout-wrapper">
    <NavigationBar/>
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page Title -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between bg-light p-4 rounded-3 shadow-sm">
                <div>
                  <h4 class="mb-2 font-size-20">
                    <i class="bx bx-user-circle text-primary me-2"></i>
                    Client Database — Unified Profile
                  </h4>
                  <p class="text-muted mb-0">View a client's profile linked across all revenue streams</p>
                </div>
                <div class="page-title-right d-flex align-items-center gap-2">
                  <button v-if="profileData" @click="backToList" class="btn btn-outline-secondary btn-sm">
                    <i class="bx bx-arrow-back me-1"></i>Back to List
                  </button>
                  <ol class="breadcrumb m-0 bg-transparent">
                    <li class="breadcrumb-item">
                      <a href="javascript:void(0);" @click="gotTo('dashboard')" class="text-decoration-none">Dashboard</a>
                    </li>
                    <li class="breadcrumb-item active">Client Database</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Search & Filter Bar (always visible) -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card border-0 shadow-sm">
                <div class="card-body py-3">
                  <div class="row g-2 align-items-center">
                    <div class="col-md-6">
                      <div class="input-group">
                        <span class="input-group-text bg-light"><i class="bx bx-search"></i></span>
                        <input
                            v-model="tableSearch"
                            type="text"
                            class="form-control"
                            placeholder="Search by name, phone, email..."
                            @input="onTableSearchInput"
                            @keyup.enter="loadClients(1)"
                        />
                        <button v-if="tableSearch" class="btn btn-outline-secondary" @click="clearSearch">
                          <i class="bx bx-x"></i>
                        </button>
                      </div>
                    </div>
                    <div class="col-md-2">
                      <select v-model="perPage" class="form-select" @change="loadClients(1)">
                        <option value="10">10 per page</option>
                        <option value="20">20 per page</option>
                        <option value="50">50 per page</option>
                        <option value="100">100 per page</option>
                      </select>
                    </div>
                    <div class="col-md-4 text-end">
                      <span class="text-muted me-3">
                        <i class="bx bx-group me-1"></i>
                        <strong>{{ totalClients }}</strong> clients total
                      </span>
                      <button @click="loadClients(currentPage)" class="btn btn-outline-primary btn-sm" :disabled="tableLoading">
                        <i class="bx" :class="tableLoading ? 'bx-loader-alt bx-spin' : 'bx-refresh'"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ============================= -->
          <!-- DEFAULT: CLIENTS TABLE        -->
          <!-- ============================= -->
          <div v-if="!profileData">
            <div class="row">
              <div class="col-12">
                <div class="card border-0 shadow-sm">
                  <div class="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                    <h6 class="mb-0"><i class="bx bx-list-ul text-primary me-2"></i>All Clients</h6>
                    <small class="text-muted">Click any row to view full profile</small>
                  </div>
                  <div class="card-body p-0">

                    <div v-if="tableLoading && clients.length === 0" class="text-center py-5">
                      <div class="spinner-border text-primary mb-3" role="status" style="width:3rem;height:3rem;"></div>
                      <p class="text-muted">Loading clients...</p>
                    </div>

                    <div v-else class="table-responsive">
                      <table class="table table-hover clients-table mb-0">
                        <thead class="table-light">
                        <tr>
                          <th style="width:50px;">#</th>
                          <th>Client Name</th>
                          <th>Phone Number</th>
                          <th>Email</th>
                          <th class="text-center">Streams</th>
                          <th class="text-center">Bills</th>
                          <th class="text-end">Total Paid</th>
                          <th class="text-end">Balance</th>
                          <th>Registered</th>
                          <th class="text-center">Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr
                            v-for="(client, idx) in clients"
                            :key="client.id"
                            class="client-row"
                            @click="openProfile(client)"
                            style="cursor:pointer;"
                        >
                          <td class="text-muted">{{ ((currentPage - 1) * perPage) + idx + 1 }}</td>
                          <td>
                            <div class="d-flex align-items-center gap-2">
                              <div class="mini-avatar">{{ getInitials(client.names) }}</div>
                              <span class="fw-semibold">{{ client.names }}</span>
                            </div>
                          </td>
                          <td><span class="font-monospace text-primary">{{ client.phoneNumber }}</span></td>
                          <td><small class="text-muted">{{ (client.email && client.email !== '#') ? client.email : '—' }}</small></td>
                          <td class="text-center">
                            <span v-if="client.streamCount > 0" class="badge bg-soft-primary text-primary">{{ client.streamCount }}</span>
                            <span v-else class="text-muted">—</span>
                          </td>
                          <td class="text-center">
                            <span v-if="client.billCount > 0" class="badge bg-soft-info text-info">{{ client.billCount }}</span>
                            <span v-else class="text-muted">—</span>
                          </td>
                          <td class="text-end text-success fw-semibold">
                            {{ client.totalPaid > 0 ? formatPriceShort(client.totalPaid) : '—' }}
                          </td>
                          <td class="text-end">
                              <span v-if="parseFloat(client.totalBalance) > 0" class="text-danger fw-semibold">
                                {{ formatPriceShort(client.totalBalance) }}
                              </span>
                            <span v-else class="badge bg-soft-success text-success">Cleared</span>
                          </td>
                          <td><small class="text-muted">{{ formatDate(client.dateCreated) }}</small></td>
                          <td class="text-center" @click.stop>
                            <button class="btn btn-sm btn-outline-primary" @click="openProfile(client)">
                              <i class="bx bx-show me-1"></i>View
                            </button>
                          </td>
                        </tr>
                        <tr v-if="!tableLoading && clients.length === 0">
                          <td colspan="10" class="text-center py-5 text-muted">
                            <i class="bx bx-user-x" style="font-size:3rem;opacity:0.3;"></i>
                            <p class="mt-2">No clients found{{ tableSearch ? ' matching "' + tableSearch + '"' : '' }}</p>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="totalPages > 1" class="card-footer bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <small class="text-muted">
                        Showing {{ ((currentPage - 1) * perPage) + 1 }}–{{ Math.min(currentPage * perPage, totalClients) }}
                        of <strong>{{ totalClients }}</strong> clients
                      </small>
                      <nav>
                        <ul class="pagination pagination-sm mb-0">
                          <li class="page-item" :class="{ disabled: currentPage === 1 }">
                            <a class="page-link" href="javascript:void(0);" @click="loadClients(1)">
                              <i class="bx bx-chevrons-left"></i>
                            </a>
                          </li>
                          <li class="page-item" :class="{ disabled: currentPage === 1 }">
                            <a class="page-link" href="javascript:void(0);" @click="loadClients(currentPage - 1)">Prev</a>
                          </li>
                          <li v-for="p in visiblePages" :key="p" class="page-item" :class="{ active: p === currentPage, disabled: p === '...' }">
                            <a class="page-link" href="javascript:void(0);" @click="p !== '...' && loadClients(p)">{{ p }}</a>
                          </li>
                          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                            <a class="page-link" href="javascript:void(0);" @click="loadClients(currentPage + 1)">Next</a>
                          </li>
                          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                            <a class="page-link" href="javascript:void(0);" @click="loadClients(totalPages)">
                              <i class="bx bx-chevrons-right"></i>
                            </a>
                          </li>
                        </ul>
                      </nav>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ============================= -->
          <!-- CLIENT PROFILE VIEW           -->
          <!-- ============================= -->
          <div v-else>

            <div v-if="profileLoading" class="text-center py-5">
              <div class="spinner-border text-primary mb-3" role="status" style="width:3rem;height:3rem;"></div>
              <p class="text-muted h6">Loading client profile...</p>
            </div>

            <div v-else>

              <!-- Hero Card -->
              <div class="row mb-4">
                <div class="col-12">
                  <div class="card border-0 shadow-sm client-hero-card">
                    <div class="card-body py-4">
                      <div class="row align-items-center">
                        <div class="col-auto">
                          <div class="client-avatar">{{ getInitials(profileData.customer.names) }}</div>
                        </div>
                        <div class="col">
                          <h4 class="mb-1 text-white fw-bold">{{ profileData.customer.names }}</h4>
                          <div class="d-flex flex-wrap gap-3 mt-2">
                            <span class="client-badge"><i class="bx bx-phone me-1"></i>{{ profileData.customer.phoneNumber }}</span>
                            <span v-if="profileData.customer.email && profileData.customer.email !== '#'" class="client-badge">
                              <i class="bx bx-envelope me-1"></i>{{ profileData.customer.email }}
                            </span>
                            <span v-if="profileData.customer.dateCreated" class="client-badge">
                              <i class="bx bx-calendar me-1"></i>Registered {{ formatDate(profileData.customer.dateCreated) }}
                            </span>
                          </div>
                        </div>
                        <div class="col-auto d-none d-md-block text-end">
                          <div class="text-white opacity-75 small mb-1">Linked Revenue Streams</div>
                          <div class="display-6 fw-bold text-white">{{ profileData.summary.totalStreams }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Summary Cards -->
              <div class="row mb-4">
                <div class="col-6 col-lg-3">
                  <div class="card border-0 shadow-sm summary-card h-100">
                    <div class="card-body text-center py-4">
                      <div class="summary-icon bg-soft-primary text-primary mx-auto mb-3"><i class="bx bx-layer"></i></div>
                      <h5 class="mb-1 fw-bold">{{ profileData.summary.totalStreams }}</h5>
                      <p class="text-muted mb-0 small">Revenue Streams</p>
                    </div>
                  </div>
                </div>
                <div class="col-6 col-lg-3">
                  <div class="card border-0 shadow-sm summary-card h-100">
                    <div class="card-body text-center py-4">
                      <div class="summary-icon bg-soft-info text-info mx-auto mb-3"><i class="bx bx-receipt"></i></div>
                      <h5 class="mb-1 fw-bold">{{ profileData.summary.totalBills }}</h5>
                      <p class="text-muted mb-0 small">Total Bills</p>
                    </div>
                  </div>
                </div>
                <div class="col-6 col-lg-3">
                  <div class="card border-0 shadow-sm summary-card h-100">
                    <div class="card-body text-center py-4">
                      <div class="summary-icon bg-soft-success text-success mx-auto mb-3"><i class="bx bx-check-shield"></i></div>
                      <h5 class="mb-1 fw-bold">{{ formatPrice(profileData.summary.totalPaid) }}</h5>
                      <p class="text-muted mb-0 small">Total Paid</p>
                    </div>
                  </div>
                </div>
                <div class="col-6 col-lg-3">
                  <div class="card border-0 shadow-sm summary-card h-100">
                    <div class="card-body text-center py-4">
                      <div class="summary-icon mx-auto mb-3"
                           :class="profileData.summary.totalBalance > 0 ? 'bg-soft-danger text-danger' : 'bg-soft-success text-success'">
                        <i class="bx" :class="profileData.summary.totalBalance > 0 ? 'bx-minus-circle' : 'bx-check-circle'"></i>
                      </div>
                      <h5 class="mb-1 fw-bold" :class="profileData.summary.totalBalance > 0 ? 'text-danger' : 'text-success'">
                        {{ formatPrice(profileData.summary.totalBalance) }}
                      </h5>
                      <p class="text-muted mb-0 small">Outstanding Balance</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Clearance Rate -->
              <div class="row mb-4">
                <div class="col-12">
                  <div class="card border-0 shadow-sm">
                    <div class="card-body">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <h6 class="mb-0 fw-semibold"><i class="bx bx-trending-up text-primary me-2"></i>Overall Payment Clearance Rate</h6>
                        <span class="fw-bold fs-5" :class="getClearanceClass(profileData.summary.clearanceRate)">{{ profileData.summary.clearanceRate }}%</span>
                      </div>
                      <div class="progress" style="height:14px;border-radius:7px;">
                        <div class="progress-bar" role="progressbar"
                             :style="{ width: Math.min(profileData.summary.clearanceRate, 100) + '%' }"
                             :class="getClearanceBarClass(profileData.summary.clearanceRate)"></div>
                      </div>
                      <div class="d-flex justify-content-between mt-2">
                        <small class="text-muted">Total Billed: {{ formatPrice(profileData.summary.totalBilled) }}</small>
                        <small class="text-muted">Total Paid: {{ formatPrice(profileData.summary.totalPaid) }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Stream Cards -->
              <div class="row mb-4">
                <div class="col-12">
                  <h6 class="fw-bold text-uppercase text-muted mb-3" style="letter-spacing:1px;font-size:0.75rem;">
                    <i class="bx bx-link-alt me-2"></i>Linked Revenue Streams
                  </h6>
                </div>
                <div v-for="stream in profileData.streams" :key="stream.incomeTypePrefix" class="col-md-6 col-lg-4 mb-3">
                  <div class="card border-0 shadow-sm stream-card h-100" :class="{ 'stream-card--active': activeStream === stream.incomeTypePrefix }">
                    <div class="card-body">
                      <div class="d-flex justify-content-between align-items-start mb-3">
                        <div class="d-flex align-items-center gap-2">
                          <div class="stream-icon" :style="{ background: getStreamColor(stream.incomeTypePrefix) }">
                            <i class="bx" :class="getStreamIcon(stream.incomeTypePrefix)"></i>
                          </div>
                          <div>
                            <h6 class="mb-0 fw-bold">{{ stream.streamName }}</h6>
                            <small class="text-muted">{{ stream.incomeTypePrefix }}</small>
                          </div>
                        </div>
                        <span class="badge" :class="getStreamStatusBadge(stream)">{{ getStreamStatusLabel(stream) }}</span>
                      </div>
                      <div class="account-ref-box mb-3">
                        <small class="text-muted d-block mb-1">{{ getAccountLabel(stream.incomeTypePrefix) }}</small>
                        <span class="fw-bold text-primary font-monospace">{{ stream.accountRef || 'N/A' }}</span>
                      </div>
                      <div class="row g-2 mb-3">
                        <div class="col-4 text-center">
                          <small class="text-muted d-block" style="font-size:0.7rem;">BILLED</small>
                          <span class="fw-semibold" style="font-size:0.85rem;">{{ formatPriceShort(stream.totalBilled) }}</span>
                        </div>
                        <div class="col-4 text-center border-start border-end">
                          <small class="text-muted d-block" style="font-size:0.7rem;">PAID</small>
                          <span class="fw-semibold text-success" style="font-size:0.85rem;">{{ formatPriceShort(stream.totalPaid) }}</span>
                        </div>
                        <div class="col-4 text-center">
                          <small class="text-muted d-block" style="font-size:0.7rem;">BALANCE</small>
                          <span class="fw-semibold" :class="parseFloat(stream.totalBalance) > 0 ? 'text-danger' : 'text-success'" style="font-size:0.85rem;">
                            {{ formatPriceShort(stream.totalBalance) }}
                          </span>
                        </div>
                      </div>
                      <div class="d-flex justify-content-between align-items-center">
                        <small class="text-muted">{{ stream.billCount }} bill(s) · {{ stream.latestYear || '—' }}</small>
                        <button class="btn btn-sm btn-outline-primary" @click="expandStream(stream)">
                          <i class="bx bx-expand-alt me-1"></i>View All
                        </button>
                      </div>

                      <!-- Entity Info Panel -->
                      <div v-if="stream.entity" class="entity-panel mt-3">
                        <div class="entity-panel-header">
                          <i class="bx me-1" :class="getEntityIcon(stream.entity._type)"></i>
                          {{ getEntityLabel(stream.entity._type) }}
                        </div>

                        <!-- BUSINESS (SBP / Fire / Liquor) -->
                        <template v-if="stream.entity._type === 'BUSINESS'">
                          <div class="entity-row"><span>Business</span><strong>{{ stream.entity.businessName }}</strong></div>
                          <div class="entity-row" v-if="stream.entity.businessCategory"><span>Category</span><span>{{ stream.entity.businessCategory }}</span></div>
                          <div class="entity-row" v-if="stream.entity.physicalAddress"><span>Address</span><span>{{ stream.entity.physicalAddress }}</span></div>
                          <div class="entity-row" v-if="stream.entity.fullNames"><span>Owner</span><span>{{ stream.entity.fullNames }}</span></div>
                          <div class="entity-row" v-if="stream.entity.businessPhone"><span>Phone</span><span>{{ stream.entity.businessPhone }}</span></div>
                          <div class="entity-row" v-if="stream.entity.subCountyName"><span>Sub-County</span><span>{{ stream.entity.subCountyName }}</span></div>
                          <div class="entity-row" v-if="stream.entity.businessStatus">
                            <span>Status</span>
                            <span class="badge" :class="stream.entity.businessStatus === 'ACTIVE' ? 'bg-success' : 'bg-warning text-dark'">
                              {{ stream.entity.businessStatus }}
                            </span>
                          </div>
                          <div class="entity-row" v-if="stream.entity.lastRenewed"><span>Last Renewed</span><small>{{ stream.entity.lastRenewed }}</small></div>
                        </template>

                        <!-- VEHICLE (PKN / Parking) -->
                        <template v-else-if="stream.entity._type === 'VEHICLE'">
                          <div class="entity-row"><span>Plate</span><strong class="font-monospace text-primary">{{ stream.entity.numberPlate }}</strong></div>
                          <div class="entity-row" v-if="stream.entity.category"><span>Category</span><span>{{ stream.entity.category }}</span></div>
                          <div class="entity-row" v-if="stream.entity.owner"><span>Owner</span><span>{{ stream.entity.owner }}</span></div>
                          <div class="entity-row" v-if="stream.entity.phoneNumber"><span>Phone</span><span>{{ stream.entity.phoneNumber }}</span></div>
                          <div class="entity-row" v-if="stream.entity.saccoName"><span>SACCO</span><span>{{ stream.entity.saccoName }}</span></div>
                          <div class="entity-row" v-if="stream.entity.subCountyName"><span>Sub-County</span><span>{{ stream.entity.subCountyName }}</span></div>
                          <div class="entity-row" v-if="stream.entity.zone"><span>Zone</span><span>{{ stream.entity.zone }}</span></div>
                        </template>

                        <!-- LAND PROPERTY (LANDRATE) -->
                        <template v-else-if="stream.entity._type === 'LAND_PROPERTY'">
                          <div class="entity-row" v-if="stream.entity.UPN"><span>UPN</span><strong class="font-monospace">{{ stream.entity.UPN }}</strong></div>
                          <div class="entity-row" v-if="stream.entity.LRNumber"><span>LR Number</span><span>{{ stream.entity.LRNumber }}</span></div>
                          <div class="entity-row"><span>Owner</span><strong>{{ stream.entity.ownerNames }}</strong></div>
                          <div class="entity-row" v-if="stream.entity.ownerPhoneNo"><span>Phone</span><span>{{ stream.entity.ownerPhoneNo }}</span></div>
                          <div class="entity-row" v-if="stream.entity.percelAddress"><span>Address</span><span>{{ stream.entity.percelAddress }}</span></div>
                          <div class="entity-row" v-if="stream.entity.percelSize"><span>Size</span><span>{{ stream.entity.percelSize }}</span></div>
                          <div class="entity-row" v-if="stream.entity.subCountyName"><span>Sub-County</span><span>{{ stream.entity.subCountyName }}</span></div>
                          <div class="entity-row" v-if="stream.entity.useName"><span>Use</span><span>{{ stream.entity.useName }}</span></div>
                        </template>

                        <!-- RENTAL UNIT (HSRENT) -->
                        <template v-else-if="stream.entity._type === 'RENTAL_UNIT'">
                          <div class="entity-row"><span>Unit No</span><strong class="text-primary font-monospace">{{ stream.entity.unitNo }}</strong></div>
                          <div class="entity-row" v-if="stream.entity.property"><span>Property</span><span>{{ stream.entity.property }}</span></div>
                          <div class="entity-row" v-if="stream.entity.category"><span>Category</span><span>{{ stream.entity.category }}</span></div>
                          <div class="entity-row" v-if="stream.entity.occupants"><span>Occupant</span><strong>{{ stream.entity.occupants }}</strong></div>
                          <div class="entity-row" v-if="stream.entity.phoneNumber"><span>Phone</span><span>{{ stream.entity.phoneNumber }}</span></div>
                          <div class="entity-row"><span>Monthly Rent</span><strong class="text-success">KES {{ Number(stream.entity.monthlyRent).toLocaleString() }}</strong></div>
                          <div class="entity-row" v-if="stream.entity.arrears > 0"><span>Arrears</span><strong class="text-danger">KES {{ Number(stream.entity.arrears).toLocaleString() }}</strong></div>
                          <div class="entity-row">
                            <span>Status</span>
                            <span class="badge" :class="stream.entity.status === 'OCCUPIED' ? 'bg-success' : 'bg-secondary'">{{ stream.entity.status }}</span>
                          </div>
                          <div class="entity-row" v-if="stream.entity.subCountyName"><span>Sub-County</span><span>{{ stream.entity.subCountyName }}</span></div>
                        </template>

                        <!-- PLOT (PLTRENT) -->
                        <template v-else-if="stream.entity._type === 'PLOT'">
                          <div class="entity-row"><span>Plot No</span><strong class="font-monospace text-primary">{{ stream.entity.plotNumber }}</strong></div>
                          <div class="entity-row"><span>Owner</span><strong>{{ stream.entity.ownerNames }}</strong></div>
                          <div class="entity-row" v-if="stream.entity.ownerPhoneNo"><span>Phone</span><span>{{ stream.entity.ownerPhoneNo }}</span></div>
                          <div class="entity-row" v-if="stream.entity.plotAddress"><span>Address</span><span>{{ stream.entity.plotAddress }}</span></div>
                          <div class="entity-row" v-if="stream.entity.plotSize"><span>Size</span><span>{{ stream.entity.plotSize }}</span></div>
                          <div class="entity-row" v-if="stream.entity.subCountyName"><span>Sub-County</span><span>{{ stream.entity.subCountyName }}</span></div>
                          <div class="entity-row"><span>Annual Rent</span><strong>KES {{ Number(stream.entity.annualRent).toLocaleString() }}</strong></div>
                          <div class="entity-row">
                            <span>Status</span>
                            <span class="badge" :class="stream.entity.status === 'Active' ? 'bg-success' : 'bg-secondary'">{{ stream.entity.status }}</span>
                          </div>
                        </template>

                      </div>
                      <!-- No entity found notice -->
                      <div v-else-if="isLinkedStream(stream.incomeTypePrefix)" class="entity-panel mt-3 text-center py-2">
                        <small class="text-muted"><i class="bx bx-unlink me-1"></i>No linked record found (Ref: {{ stream.accountRef }})</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Stream Expand Drawer -->
              <div v-if="expandedStream" class="row mb-4">
                <div class="col-12">
                  <div class="card border-0 shadow-sm border-top border-primary border-3">
                    <div class="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                      <div class="d-flex align-items-center gap-2">
                        <div class="stream-icon" :style="{ background: getStreamColor(expandedStream.incomeTypePrefix) }">
                          <i class="bx" :class="getStreamIcon(expandedStream.incomeTypePrefix)"></i>
                        </div>
                        <div>
                          <h6 class="mb-0 fw-bold">{{ expandedStream.streamName }} — Bill History</h6>
                          <small class="text-muted">Account Ref: <strong>{{ expandedStream.accountRef }}</strong></small>
                        </div>
                      </div>
                      <button class="btn btn-sm btn-outline-secondary" @click="expandedStream = null; expandedBills = []">
                        <i class="bx bx-x me-1"></i>Close
                      </button>
                    </div>
                    <div v-if="expandLoading" class="text-center py-4">
                      <div class="spinner-border text-primary" role="status"></div>
                    </div>
                    <div v-else class="card-body p-0">
                      <div v-if="expandedBills.length === 0" class="text-center py-5 text-muted">
                        <i class="bx bx-receipt" style="font-size:3rem;opacity:0.3;"></i>
                        <p class="mt-2">No bills found</p>
                      </div>
                      <div v-else class="table-responsive">
                        <table class="table table-hover table-striped mb-0">
                          <thead class="table-light">
                          <tr>
                            <th>#</th><th>Bill No</th><th>Account / ID</th><th>Description</th>
                            <th>FY</th><th>Zone</th>
                            <th class="text-end">Billed</th><th class="text-end">Paid</th>
                            <th class="text-end">Balance</th><th class="text-center">Status</th><th>Date</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="(bill, idx) in expandedBills" :key="bill.id">
                            <td class="text-muted">{{ idx + 1 }}</td>
                            <td><span class="badge bg-light text-dark font-monospace">{{ bill.billNo }}</span></td>
                            <td>
                                <span
                                    v-if="isLinkedRef(bill.customer || bill.idNo)"
                                    class="fw-semibold text-primary linked-ref"
                                    @click="openEntityModal(bill.customer || bill.idNo, expandedStream.incomeTypePrefix)"
                                    title="Click to view details"
                                >
                                  <i class="bx bx-link-external me-1" style="font-size:0.75rem;"></i>{{ bill.customer || bill.idNo }}
                                </span>
                              <span v-else class="fw-semibold text-primary">{{ bill.customer || bill.idNo }}</span>
                            </td>
                            <td><div style="max-width:200px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;" :title="bill.feeDescription || bill.description">{{ bill.feeDescription || bill.description || '—' }}</div></td>
                            <td><span class="badge bg-soft-secondary text-secondary">{{ bill.fiscalYear || '—' }}</span></td>
                            <td><small class="text-muted">{{ bill.zone || '—' }}</small></td>
                            <td class="text-end">{{ formatPrice(bill.billTotal) }}</td>
                            <td class="text-end text-success fw-semibold">{{ formatPrice(bill.receiptAmount) }}</td>
                            <td class="text-end" :class="parseFloat(bill.billBalance) > 0 ? 'text-danger fw-semibold' : 'text-success'">{{ formatPrice(bill.billBalance) }}</td>
                            <td class="text-center"><span class="badge" :class="getBillStatusBadge(bill.status)">{{ bill.status }}</span></td>
                            <td><small class="text-muted">{{ formatDate(bill.dateCreated) }}</small></td>
                          </tr>
                          </tbody>
                          <tfoot class="table-light fw-bold">
                          <tr>
                            <td colspan="6" class="text-end">Totals:</td>
                            <td class="text-end">{{ formatPrice(expandedStream.totalBilled) }}</td>
                            <td class="text-end text-success">{{ formatPrice(expandedStream.totalPaid) }}</td>
                            <td class="text-end" :class="parseFloat(expandedStream.totalBalance) > 0 ? 'text-danger' : 'text-success'">{{ formatPrice(expandedStream.totalBalance) }}</td>
                            <td colspan="2"></td>
                          </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Recent Payments -->
              <div class="row mb-4" v-if="profileData.receipts && profileData.receipts.length > 0">
                <div class="col-12">
                  <div class="card border-0 shadow-sm">
                    <div class="card-header bg-white border-bottom">
                      <h6 class="mb-0"><i class="bx bx-history text-primary me-2"></i>Recent Payment Activity</h6>
                    </div>
                    <div class="card-body p-0">
                      <div class="table-responsive">
                        <table class="table table-hover mb-0">
                          <thead class="table-light">
                          <tr>
                            <th>Receipt No</th><th>Stream</th><th>Mode</th>
                            <th>Transaction Code</th><th>Paid By</th>
                            <th class="text-end">Amount</th><th>Date</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="r in profileData.receipts" :key="r.id">
                            <td><span class="badge bg-light text-dark font-monospace">{{ r.receiptNo }}</span></td>
                            <td><span class="badge bg-soft-primary text-primary">{{ r.streamName }}</span></td>
                            <td><span class="badge" :class="getPaymentModeBadge(r.paymentMode)">{{ r.paymentMode || '—' }}</span></td>
                            <td><small class="text-muted font-monospace">{{ r.transactionCode || '—' }}</small></td>
                            <td><small>{{ r.paidBy || '—' }}</small></td>
                            <td class="text-end fw-bold text-success">{{ formatPrice(r.amount) }}</td>
                            <td><small class="text-muted">{{ formatDateTime(r.dateCreated) }}</small></td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">© {{ new Date().getFullYear() }} County Revenue System</div>
            <div class="col-sm-6"><div class="text-sm-end d-sm-block">Powered by County IT Department</div></div>
          </div>
        </div>
      </footer>

      <!-- ===== ENTITY DETAIL MODAL ===== -->
      <div v-if="entityModal.show" class="em-overlay" @click.self="closeEntityModal">
        <div class="em-sheet">

          <!-- Gradient Header -->
          <div class="em-header" :class="'em-header--' + (entityModal.type || 'default').toLowerCase()">
            <div class="em-header-bg"></div>
            <div class="em-header-content">
              <div class="em-header-icon">
                <i class="bx" :class="getEntityIcon(entityModal.type)"></i>
              </div>
              <div class="em-header-text">
                <!-- Business: show name prominently -->
                <template v-if="entityModal.data && entityModal.type === 'BUSINESS'">
                  <div class="em-header-label">Single Business Permit</div>
                  <div class="em-header-title">{{ entityModal.data.businessName }}</div>
                  <div class="em-header-meta">
                    <span><i class="bx bx-id-card me-1"></i>ID {{ entityModal.data.businessID }}</span>
                    <span v-if="entityModal.data.subCountyName"><i class="bx bx-map-pin me-1"></i>{{ entityModal.data.subCountyName }}</span>
                    <span v-if="entityModal.data.businessStatus">
                      <span class="em-status-pill" :class="entityModal.data.businessStatus === 'ACTIVE' ? 'em-pill--green' : 'em-pill--orange'">
                        {{ entityModal.data.businessStatus }}
                      </span>
                    </span>
                  </div>
                </template>
                <template v-else>
                  <div class="em-header-label">{{ getEntityLabel(entityModal.type) }}</div>
                  <div class="em-header-title">{{ entityModal.ref }}</div>
                </template>
              </div>
              <button class="em-close" @click="closeEntityModal"><i class="bx bx-x"></i></button>
            </div>

            <!-- Tabs (only for business) -->
            <div v-if="entityModal.type === 'BUSINESS' && entityModal.data" class="em-tabs">
              <button v-for="tab in businessTabs" :key="tab.key"
                      class="em-tab" :class="{ 'em-tab--active': entityModal.activeTab === tab.key }"
                      @click="entityModal.activeTab = tab.key">
                <i class="bx me-1" :class="tab.icon"></i>{{ tab.label }}
                <span v-if="tab.key === 'permits' && entityModal.data.permits" class="em-tab-badge">{{ entityModal.data.permits.length }}</span>
              </button>
            </div>
          </div>

          <!-- Loading -->
          <div v-if="entityModal.loading" class="em-loading">
            <div class="em-spinner"></div>
            <p>Fetching details...</p>
          </div>

          <!-- No data -->
          <div v-else-if="!entityModal.data" class="em-empty">
            <i class="bx bx-search-alt"></i>
            <p>No record found for <strong>{{ entityModal.ref }}</strong></p>
          </div>

          <!-- ── BUSINESS BODY ── -->
          <div v-else-if="entityModal.type === 'BUSINESS'" class="em-body">

            <!-- TAB: Overview -->
            <div v-show="entityModal.activeTab === 'overview'">
              <!-- Quick stats row -->
              <div class="em-stats-row">
                <div class="em-stat">
                  <div class="em-stat-val">{{ entityModal.data.businessCategory ? entityModal.data.businessCategory.split('-')[0].trim() : '—' }}</div>
                  <div class="em-stat-lbl">Category</div>
                </div>
                <div class="em-stat">
                  <div class="em-stat-val">{{ entityModal.data.numberOfEmployees || '—' }}</div>
                  <div class="em-stat-lbl">Employees</div>
                </div>
                <div class="em-stat">
                  <div class="em-stat-val">{{ entityModal.data.lastRenewed || '—' }}</div>
                  <div class="em-stat-lbl">Last Renewed</div>
                </div>
                <div class="em-stat">
                  <div class="em-stat-val">{{ entityModal.data.permitsCount || '—' }}</div>
                  <div class="em-stat-lbl">Total Permits</div>
                </div>
              </div>
              <!-- Quick Actions -->
              <div class="em-quick-actions">
                <button class="em-action-btn em-action-btn--primary" @click="printLatestPermit(entityModal.data.businessID)">
                  <i class="bx bx-printer me-2"></i>Print Latest Permit
                </button>
                <button class="em-action-btn em-action-btn--outline" @click="entityModal.activeTab = 'permits'">
                  <i class="bx bx-badge-check me-2"></i>View All Permits
                </button>
              </div>

              <!-- Info sections -->
              <div class="em-section">
                <div class="em-section-title"><i class="bx bx-store me-2"></i>Business Details</div>
                <div class="em-fields">
                  <div class="em-field em-field--wide"><div class="em-field-lbl">Business Name</div><div class="em-field-val em-field-val--strong">{{ entityModal.data.businessName }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Category</div><div class="em-field-val">{{ entityModal.data.businessCategory || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Sub-Category</div><div class="em-field-val">{{ entityModal.data.businessSubCategory || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Business Type</div><div class="em-field-val">{{ entityModal.data.businessType || entityModal.data.businessTypeName || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">KRA PIN</div><div class="em-field-val em-mono">{{ entityModal.data.kraPin || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Registered</div><div class="em-field-val">{{ entityModal.data.dateCreated || '—' }}</div></div>
                </div>
              </div>

              <div class="em-section">
                <div class="em-section-title"><i class="bx bx-map me-2"></i>Location & Premises</div>
                <div class="em-fields">
                  <div class="em-field"><div class="em-field-lbl">Physical Address</div><div class="em-field-val">{{ entityModal.data.physicalAddress || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Plot Number</div><div class="em-field-val em-mono">{{ entityModal.data.plotNumber || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Building</div><div class="em-field-val">{{ entityModal.data.buildingName || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Floor / Room</div><div class="em-field-val">{{ [entityModal.data.floorNo, entityModal.data.roomNo].filter(Boolean).join(' / ') || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Occupancy</div><div class="em-field-val">{{ entityModal.data.buildingOccupancy || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Premise Size</div><div class="em-field-val">{{ entityModal.data.premiseSize || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Sub-County</div><div class="em-field-val">{{ entityModal.data.subCountyName || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Ward</div><div class="em-field-val">{{ entityModal.data.wardName || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Zone</div><div class="em-field-val">{{ entityModal.data.zone || '—' }}</div></div>
                </div>
              </div>

              <div class="em-section">
                <div class="em-section-title"><i class="bx bx-user me-2"></i>Owner / Contact</div>
                <div class="em-fields">
                  <div class="em-field em-field--wide"><div class="em-field-lbl">Owner Names</div><div class="em-field-val em-field-val--strong">{{ entityModal.data.fullNames || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Owner Phone</div><div class="em-field-val em-mono text-primary">{{ entityModal.data.ownerPhone || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Owner Email</div><div class="em-field-val">{{ entityModal.data.ownerEmail || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Business Phone</div><div class="em-field-val em-mono">{{ entityModal.data.businessPhone || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Postal Address</div><div class="em-field-val">{{ entityModal.data.postalAddress || '—' }}</div></div>
                </div>
              </div>

              <div v-if="entityModal.data.businessDes && entityModal.data.businessDes.length < 300" class="em-section">
                <div class="em-section-title"><i class="bx bx-info-circle me-2"></i>Description</div>
                <p class="em-description">{{ entityModal.data.businessDes }}</p>
              </div>
            </div>

            <!-- TAB: Permits -->
            <div v-show="entityModal.activeTab === 'permits'">
              <div v-if="!entityModal.data.permits || entityModal.data.permits.length === 0" class="em-empty-tab">
                <i class="bx bx-folder-open"></i>
                <p>No permit records found</p>
              </div>
              <div v-else class="em-permits-list">
                <div v-for="p in entityModal.data.permits" :key="p.id" class="em-permit-card"
                     :class="getPermitCardClass(p)">
                  <div class="em-permit-left">
                    <div class="em-permit-icon"><i class="bx bx-badge-check"></i></div>
                    <div>
                      <div class="em-permit-name">{{ p.feeDescription || 'Permit' }}</div>
                      <div class="em-permit-meta">
                        <span class="em-mono">{{ p.billNo }}</span>
                        <span>FY {{ p.fiscalYear || p.year || '—' }}</span>
                        <span v-if="p.duration">{{ p.duration }}</span>
                      </div>
                      <div class="em-permit-dates">
                        <span><i class="bx bx-calendar-check me-1"></i>{{ formatDate(p.startDate) }}</span>
                        <span class="em-permit-arrow">→</span>
                        <span :class="isExpired(p.endDate) ? 'text-danger' : 'text-success'">
                          <i class="bx me-1" :class="isExpired(p.endDate) ? 'bx-calendar-x' : 'bx-calendar'"></i>
                          {{ formatDate(p.endDate) }}
                          <span v-if="isExpired(p.endDate)" class="ms-1">⚠ Expired</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="em-permit-right">
                    <div class="em-permit-amount">
                      <div class="em-permit-paid">Ksh {{ Number(p.receiptAmount || 0).toLocaleString() }}</div>
                      <div v-if="parseFloat(p.billBalance) > 0" class="em-permit-balance">Ksh {{ Number(p.billBalance).toLocaleString() }} due</div>
                    </div>
                    <span class="em-permit-badge" :class="getPermitStatusBadge(p)">{{ getPermitStatusLabel(p) }}</span>
                    <div class="em-permit-actions">
                      <button class="em-print-btn" @click="printPermitByBillNo(p.billNo)" title="Print Permit">
                        <i class="bx bx-printer me-1"></i>Print Permit
                      </button>
                      <button class="em-print-btn em-print-btn--bill" @click="printBillOnly(p.billNo)" title="Print Bill">
                        <i class="bx bx-receipt me-1"></i>Bill
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- TAB: Map -->
            <div v-show="entityModal.activeTab === 'map'">
              <div v-if="!entityModal.data.lat || entityModal.data.lat == '0'" class="em-empty-tab">
                <i class="bx bx-map-alt"></i>
                <p>No coordinates available for this business</p>
              </div>
              <div v-else>
                <div class="em-map-info">
                  <i class="bx bx-map-pin text-primary me-2"></i>
                  <span>{{ entityModal.data.physicalAddress || entityModal.data.subCountyName }}, {{ entityModal.data.wardName }}</span>
                  <small class="text-muted ms-2 em-mono">{{ entityModal.data.lat }}, {{ entityModal.data.lng }}</small>
                </div>
                <div ref="bizMap" class="em-map"></div>
              </div>
            </div>

          </div>

          <!-- ── VEHICLE BODY ── -->
          <div v-else-if="entityModal.type === 'VEHICLE' && entityModal.data" class="em-body">
            <div class="em-vehicle-plate">{{ entityModal.data.numberPlate }}</div>
            <div class="em-stats-row">
              <div class="em-stat"><div class="em-stat-val">{{ entityModal.data.category || '—' }}</div><div class="em-stat-lbl">Category</div></div>
              <div class="em-stat"><div class="em-stat-val">{{ entityModal.data.saccoName || '—' }}</div><div class="em-stat-lbl">SACCO</div></div>
              <div class="em-stat"><div class="em-stat-val">{{ entityModal.data.subCountyName || '—' }}</div><div class="em-stat-lbl">Sub-County</div></div>
              <div class="em-stat"><div class="em-stat-val">Ksh {{ Number(entityModal.data.amount || 0).toLocaleString() }}</div><div class="em-stat-lbl">Annual Fee</div></div>
            </div>

            <!-- Vehicle sub-tabs -->
            <div class="em-subtabs mb-3">
              <button class="em-subtab" :class="{ 'em-subtab--active': entityModal.vehicleTab !== 'entries' }" @click="entityModal.vehicleTab = 'details'">
                <i class="bx bx-car me-1"></i>Vehicle Details
              </button>
              <button class="em-subtab" :class="{ 'em-subtab--active': entityModal.vehicleTab === 'entries' }" @click="entityModal.vehicleTab = 'entries'">
                <i class="bx bx-receipt me-1"></i>Sticker / Permit History
                <span v-if="entityModal.data.entries" class="em-tab-badge ms-1">{{ entityModal.data.entries.length }}</span>
              </button>
            </div>

            <!-- Details tab -->
            <div v-show="entityModal.vehicleTab !== 'entries'">
              <div class="em-section">
                <div class="em-section-title"><i class="bx bx-user me-2"></i>Owner Details</div>
                <div class="em-fields">
                  <div class="em-field em-field--wide"><div class="em-field-lbl">Owner</div><div class="em-field-val em-field-val--strong">{{ entityModal.data.owner || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Phone</div><div class="em-field-val em-mono text-primary">{{ entityModal.data.phoneNumber || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">SACCO</div><div class="em-field-val">{{ entityModal.data.saccoName || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">SACCO Contact</div><div class="em-field-val">{{ entityModal.data.saccoContact || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">SACCO Phone</div><div class="em-field-val em-mono">{{ entityModal.data.saccoPhone || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Ward</div><div class="em-field-val">{{ entityModal.data.wardName || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Zone</div><div class="em-field-val">{{ entityModal.data.zone || '—' }}</div></div>
                </div>
              </div>
            </div>

            <!-- Entries/stickers tab -->
            <div v-show="entityModal.vehicleTab === 'entries'">
              <div v-if="!entityModal.data.entries || entityModal.data.entries.length === 0" class="em-empty-tab">
                <i class="bx bx-folder-open"></i>
                <p>No parking sticker or permit records found</p>
              </div>
              <div v-else class="em-permits-list">
                <div v-for="e in entityModal.data.entries" :key="e.id"
                     class="em-permit-card"
                     :class="getParkingEntryCardClass(e)">
                  <div class="em-permit-left">
                    <div class="em-permit-icon"><i class="bx bx-parking"></i></div>
                    <div>
                      <div class="em-permit-name">{{ e.description || e.category || 'Parking Sticker' }}</div>
                      <div class="em-permit-meta">
                        <span class="em-mono">{{ e.parkingCode }}</span>
                        <span v-if="e.zone">Zone: {{ e.zone }}</span>
                        <span v-if="e.duration">{{ e.duration }}</span>
                        <span v-if="e.street">{{ e.street }}</span>
                      </div>
                      <div class="em-permit-dates">
                        <span><i class="bx bx-calendar-check me-1"></i>{{ formatDate(e.startDate) }}</span>
                        <span class="em-permit-arrow">→</span>
                        <span :class="isExpired(e.endDate) ? 'text-danger' : 'text-success'">
                          <i class="bx me-1" :class="isExpired(e.endDate) ? 'bx-calendar-x' : 'bx-calendar'"></i>
                          {{ formatDate(e.endDate) }}
                          <span v-if="isExpired(e.endDate)" class="ms-1">⚠ Expired</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="em-permit-right">
                    <div class="em-permit-amount">
                      <div class="em-permit-paid">Ksh {{ Number(e.receiptAmount || 0).toLocaleString() }}</div>
                      <div v-if="parseFloat(e.billBalance) > 0" class="em-permit-balance">Ksh {{ Number(e.billBalance).toLocaleString() }} due</div>
                    </div>
                    <span class="em-permit-badge" :class="getParkingEntryBadge(e)">{{ getParkingEntryLabel(e) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── LAND PROPERTY BODY ── -->
          <div v-else-if="entityModal.type === 'LAND_PROPERTY' && entityModal.data" class="em-body">

            <!-- Status alert banners -->
            <div v-if="entityModal.data.suspension" class="em-alert em-alert--danger">
              <i class="bx bx-block me-2"></i>
              <strong>SUSPENDED</strong> — {{ entityModal.data.suspension.reason }}
              <small class="ms-2 opacity-75">since {{ entityModal.data.suspension.start_date }} · {{ entityModal.data.suspension.suspended_by }}</small>
            </div>
            <div v-if="entityModal.data.rateStruck" class="em-alert em-alert--warning">
              <i class="bx bx-minus-circle me-2"></i>
              <strong>RATE STRUCK OFF</strong> — {{ entityModal.data.rateStruck.reason }}
              <small class="ms-2 opacity-75">by {{ entityModal.data.rateStruck.struck_by }}</small>
            </div>

            <!-- Stats row -->
            <div class="em-stats-row" style="grid-template-columns:repeat(5,1fr);">
              <div class="em-stat"><div class="em-stat-val em-mono" style="font-size:0.75rem;">{{ entityModal.data.UPN || '—' }}</div><div class="em-stat-lbl">UPN</div></div>
              <div class="em-stat"><div class="em-stat-val em-mono" style="font-size:0.75rem;">{{ entityModal.data.LRNumber || '—' }}</div><div class="em-stat-lbl">LR Number</div></div>
              <div class="em-stat"><div class="em-stat-val text-success fw-bold">Ksh {{ Number(entityModal.data.annualCharges || entityModal.data.annualLandRates || 0).toLocaleString() }}</div><div class="em-stat-lbl">Annual Rates</div></div>
              <div class="em-stat"><div class="em-stat-val text-danger fw-bold">Ksh {{ Number(entityModal.data.currentBalance || entityModal.data.outstandingArrears || 0).toLocaleString() }}</div><div class="em-stat-lbl">Balance</div></div>
              <div class="em-stat"><div class="em-stat-val"><span class="em-status-pill" :class="entityModal.data.status === 'Active' ? 'em-pill--green' : 'em-pill--orange'">{{ entityModal.data.status || 'Active' }}</span></div><div class="em-stat-lbl">Status</div></div>
            </div>

            <!-- Sub-tabs -->
            <div class="em-subtabs mb-3">
              <button class="em-subtab" :class="{'em-subtab--active': entityModal.landTab === 'overview'}" @click="entityModal.landTab = 'overview'">
                <i class="bx bx-info-circle me-1"></i>Overview
              </button>
              <button class="em-subtab" :class="{'em-subtab--active': entityModal.landTab === 'billing'}" @click="entityModal.landTab = 'billing'">
                <i class="bx bx-receipt me-1"></i>Billing
                <span v-if="entityModal.data.entries && entityModal.data.entries.length" class="em-tab-badge ms-1">{{ entityModal.data.entries.length }}</span>
              </button>
              <button class="em-subtab" :class="{'em-subtab--active': entityModal.landTab === 'transfers'}" @click="entityModal.landTab = 'transfers'">
                <i class="bx bx-transfer me-1"></i>Transfers
                <span v-if="entityModal.data.transfers && entityModal.data.transfers.length" class="em-tab-badge ms-1">{{ entityModal.data.transfers.length }}</span>
              </button>
              <button class="em-subtab" :class="{'em-subtab--active': entityModal.landTab === 'history'}" @click="entityModal.landTab = 'history'">
                <i class="bx bx-history me-1"></i>History
              </button>
            </div>

            <!-- ── OVERVIEW TAB ── -->
            <div v-show="entityModal.landTab === 'overview'">

              <div class="em-section">
                <div class="em-section-title"><i class="bx bx-map-alt me-2"></i>Property Details</div>
                <div class="em-fields">
                  <div class="em-field em-field--wide"><div class="em-field-lbl">Parcel Address</div><div class="em-field-val em-field-val--strong">{{ entityModal.data.percelAddress || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Sub-County</div><div class="em-field-val">{{ entityModal.data.subCountyName || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Ward</div><div class="em-field-val">{{ entityModal.data.wardName || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Zone</div><div class="em-field-val">{{ entityModal.data.zone || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Block / Plot</div><div class="em-field-val em-mono">{{ entityModal.data.blockNumber || entityModal.data.block || '—' }} / {{ entityModal.data.plotNo || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Parcel Size</div><div class="em-field-val">{{ entityModal.data.percelSize || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Land Use</div><div class="em-field-val">{{ entityModal.data.useName || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Physical Address</div><div class="em-field-val">{{ entityModal.data.physicalAddress || '—' }}</div></div>
                </div>
              </div>

              <div class="em-section">
                <div class="em-section-title"><i class="bx bx-money me-2"></i>Financial Summary</div>
                <div class="em-fields">
                  <div class="em-field"><div class="em-field-lbl">Annual Land Rates</div><div class="em-field-val text-success fw-bold">Ksh {{ Number(entityModal.data.annualLandRates || 0).toLocaleString() }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Annual Ground Rent</div><div class="em-field-val text-success fw-bold">Ksh {{ Number(entityModal.data.annualGroundRent || 0).toLocaleString() }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Land Rates Arrears</div><div class="em-field-val text-danger fw-bold">Ksh {{ Number(entityModal.data.landRatesArrears || 0).toLocaleString() }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Ground Rent Arrears</div><div class="em-field-val text-danger fw-bold">Ksh {{ Number(entityModal.data.groundRentArrears || 0).toLocaleString() }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Accumulated Penalty</div><div class="em-field-val text-danger">Ksh {{ Number(entityModal.data.accumPenalty || 0).toLocaleString() }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Accumulated Interest</div><div class="em-field-val text-danger">Ksh {{ Number(entityModal.data.accumInterest || 0).toLocaleString() }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Total Payments</div><div class="em-field-val text-success">Ksh {{ Number(entityModal.data.payments || 0).toLocaleString() }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Credit Balance</div><div class="em-field-val">Ksh {{ Number(entityModal.data.creditBalance || 0).toLocaleString() }}</div></div>
                </div>
              </div>

              <div class="em-section">
                <div class="em-section-title"><i class="bx bx-user me-2"></i>Owner Details</div>
                <div class="em-fields">
                  <div class="em-field em-field--wide"><div class="em-field-lbl">Owner Names</div><div class="em-field-val em-field-val--strong">{{ entityModal.data.ownerNames || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Phone</div><div class="em-field-val em-mono text-primary">{{ entityModal.data.ownerPhoneNo || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Email</div><div class="em-field-val">{{ entityModal.data.ownerEmail || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Owner ID / Passport</div><div class="em-field-val em-mono">{{ entityModal.data.ownerID || entityModal.data.ownerPassport || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">KRA PIN</div><div class="em-field-val em-mono">{{ entityModal.data.pinNumber || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Postal Address</div><div class="em-field-val">{{ entityModal.data.boxNo ? `P.O Box ${entityModal.data.boxNo} - ${entityModal.data.postalCode || ''}, ${entityModal.data.town || ''}` : '—' }}</div></div>
                </div>
              </div>

              <!-- Co-Owner section (only if exists) -->
              <div class="em-section" v-if="entityModal.data.coOwnerNames">
                <div class="em-section-title"><i class="bx bx-user-plus me-2"></i>Co-Owner Details</div>
                <div class="em-fields">
                  <div class="em-field em-field--wide"><div class="em-field-lbl">Co-Owner Names</div><div class="em-field-val em-field-val--strong">{{ entityModal.data.coOwnerNames }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Phone</div><div class="em-field-val em-mono">{{ entityModal.data.coOwnerPhoneNo || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Email</div><div class="em-field-val">{{ entityModal.data.coOwnerEmail || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">ID No</div><div class="em-field-val em-mono">{{ entityModal.data.coOwnerID || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">KRA PIN</div><div class="em-field-val em-mono">{{ entityModal.data.coOwnerPinNumber || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Postal Address</div><div class="em-field-val">{{ entityModal.data.coOwnerBoxNo ? `P.O Box ${entityModal.data.coOwnerBoxNo} - ${entityModal.data.coOwnerPostalCode || ''}, ${entityModal.data.coOwnerTown || ''}` : '—' }}</div></div>
                </div>
              </div>

              <!-- Subdivision info (if exists) -->
              <div class="em-section" v-if="entityModal.data.subdivision">
                <div class="em-section-title"><i class="bx bx-crop me-2"></i>Subdivision Info</div>
                <div class="em-fields">
                  <div class="em-field"><div class="em-field-lbl">Reference</div><div class="em-field-val em-mono">{{ entityModal.data.subdivision.subdivision_reference }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Date Subdivided</div><div class="em-field-val">{{ entityModal.data.subdivision.subdivision_date }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Sub-Plots Created</div><div class="em-field-val fw-bold">{{ entityModal.data.subdivision.num_subplots }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Subdivided By</div><div class="em-field-val">{{ entityModal.data.subdivision.subdivided_by }}</div></div>
                </div>
              </div>

              <!-- Map -->
              <div class="em-section" v-if="entityModal.data.lat && entityModal.data.lat != '0' && entityModal.data.lat != ''">
                <div class="em-section-title"><i class="bx bx-map me-2"></i>Location Map</div>
                <div class="em-map-info">
                  <i class="bx bx-map-pin text-primary me-2"></i>
                  {{ entityModal.data.percelAddress }}, {{ entityModal.data.wardName }}
                  <small class="text-muted ms-2 em-mono">{{ entityModal.data.lat }}, {{ entityModal.data.lng }}</small>
                </div>
                <iframe frameborder="0" scrolling="no" class="em-map"
                        :src="`https://maps.google.com/maps?q=${entityModal.data.lat},${entityModal.data.lng}&t=&z=16&ie=UTF8&iwloc=&output=embed`">
                </iframe>
              </div>

              <div class="em-section">
                <div class="em-section-title"><i class="bx bx-cog me-2"></i>Admin Info</div>
                <div class="em-fields">
                  <div class="em-field"><div class="em-field-lbl">Created By</div><div class="em-field-val">{{ entityModal.data.createdBy || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Date Created</div><div class="em-field-val">{{ formatDate(entityModal.data.dateCreated) }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Last Updated By</div><div class="em-field-val">{{ entityModal.data.updatedBy || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Last Notice Date</div><div class="em-field-val">{{ entityModal.data.lastNoticeDate || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Upload Batch Ref</div><div class="em-field-val em-mono">{{ entityModal.data.uploadBatchReference || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Upload Date</div><div class="em-field-val">{{ entityModal.data.uploadDate || '—' }}</div></div>
                </div>
              </div>
            </div>

            <!-- ── BILLING TAB ── -->
            <div v-show="entityModal.landTab === 'billing'">

              <!-- Penalty + adjustment summary banners -->
              <div v-if="entityModal.data.penalties && entityModal.data.penalties.length" class="em-alert em-alert--danger mb-3">
                <i class="bx bx-error me-2"></i>
                <strong>{{ entityModal.data.penalties.length }} Penalty Record(s)</strong> — Total:
                Ksh {{ entityModal.data.penalties.reduce((s,p) => s + parseFloat(p.penaltyAmount||0), 0).toLocaleString() }}
              </div>
              <div v-if="entityModal.data.adjustments && entityModal.data.adjustments.length" class="em-alert em-alert--info mb-3">
                <i class="bx bx-slider me-2"></i>
                <strong>{{ entityModal.data.adjustments.length }} Rate Adjustment(s)</strong> on record
              </div>

              <!-- Bill entries -->
              <div class="em-section-title mb-2"><i class="bx bx-receipt me-2"></i>Payment History</div>
              <div v-if="!entityModal.data.entries || !entityModal.data.entries.length" class="em-empty-tab">
                <i class="bx bx-folder-open"></i><p>No billing records found</p>
              </div>
              <div v-else class="em-permits-list">
                <div v-for="e in entityModal.data.entries" :key="e.id"
                     class="em-permit-card"
                     :class="parseFloat(e.billBalance) > 0 ? 'em-permit-card--unpaid' : 'em-permit-card--active'">
                  <div class="em-permit-left">
                    <div class="em-permit-icon"><i class="bx bx-file-blank"></i></div>
                    <div>
                      <div class="em-permit-name">{{ e.feeDescription || e.description || 'Land Rate Bill' }}</div>
                      <div class="em-permit-meta">
                        <span class="em-mono">{{ e.billNo }}</span>
                        <span v-if="e.paymentMode">{{ e.paymentMode }}</span>
                        <span v-if="e.transactionCode" class="em-mono">{{ e.transactionCode }}</span>
                      </div>
                      <div class="em-permit-dates">
                        <i class="bx bx-calendar me-1"></i>
                        <small class="text-muted">{{ formatDate(e.dateCreated) }}</small>
                        <span v-if="e.paidBy" class="ms-2 text-muted"><i class="bx bx-user me-1"></i>{{ e.paidBy }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="em-permit-right">
                    <div class="em-permit-amount">
                      <div class="em-permit-paid">Ksh {{ Number(e.receiptAmount || 0).toLocaleString() }}</div>
                      <div v-if="parseFloat(e.billBalance) > 0" class="em-permit-balance">Ksh {{ Number(e.billBalance).toLocaleString() }} due</div>
                    </div>
                    <span class="em-permit-badge" :class="parseFloat(e.billBalance) > 0 ? 'bg-warning' : 'bg-success'">
                      {{ parseFloat(e.billBalance) > 0 ? 'Unpaid' : 'Paid' }}
                    </span>
                    <div class="em-permit-actions">
                      <button class="em-print-btn" @click="printBillOnly(e.billNo)">
                        <i class="bx bx-printer me-1"></i>Bill
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Penalties -->
              <div v-if="entityModal.data.penalties && entityModal.data.penalties.length" class="mt-4">
                <div class="em-section-title mb-2"><i class="bx bx-error-circle me-2"></i>Penalties</div>
                <div class="em-permits-list">
                  <div v-for="p in entityModal.data.penalties" :key="p.id" class="em-permit-card em-permit-card--expired">
                    <div class="em-permit-left">
                      <div class="em-permit-icon" style="background:#fff0f0;color:#ff4757;"><i class="bx bx-error"></i></div>
                      <div>
                        <div class="em-permit-name">Penalty — FY {{ p.fiscalYear }}</div>
                        <div class="em-permit-meta">
                          <span class="em-mono">{{ p.billNo }}</span>
                          <span v-if="p.appliedBy">{{ p.appliedBy }}</span>
                        </div>
                        <small class="text-muted">{{ formatDate(p.dateApplied) }}</small>
                      </div>
                    </div>
                    <div class="em-permit-right">
                      <div class="em-permit-paid text-danger fw-bold">Ksh {{ Number(p.penaltyAmount).toLocaleString() }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Rate adjustments -->
              <div v-if="entityModal.data.adjustments && entityModal.data.adjustments.length" class="mt-4">
                <div class="em-section-title mb-2"><i class="bx bx-slider me-2"></i>Rate Adjustments</div>
                <div class="em-permits-list">
                  <div v-for="a in entityModal.data.adjustments" :key="a.id"
                       class="em-permit-card"
                       :class="a.adjustment_type === 'increase' ? 'em-permit-card--expired' : 'em-permit-card--active'">
                    <div class="em-permit-left">
                      <div class="em-permit-icon"
                           :style="a.adjustment_type === 'increase' ? 'background:#fff0f0;color:#ff4757;' : 'background:#f0fff4;color:#34c38f;'">
                        <i class="bx" :class="a.adjustment_type === 'increase' ? 'bx-up-arrow-alt' : 'bx-down-arrow-alt'"></i>
                      </div>
                      <div>
                        <div class="em-permit-name text-capitalize">{{ a.adjustment_type }} Adjustment</div>
                        <div class="em-permit-meta"><span>{{ a.reason }}</span></div>
                        <small class="text-muted">{{ formatDate(a.adjustment_date) }} · {{ a.adjusted_by }}</small>
                      </div>
                    </div>
                    <div class="em-permit-right text-end">
                      <small class="text-muted d-block">Before: Ksh {{ Number(a.amount_before).toLocaleString() }}</small>
                      <div class="em-permit-paid" :class="a.adjustment_type === 'increase' ? 'text-danger' : 'text-success'">
                        {{ a.adjustment_type === 'increase' ? '+' : '-' }}Ksh {{ Number(a.adjustment_amount).toLocaleString() }}
                      </div>
                      <small class="text-muted d-block">After: Ksh {{ Number(a.amount_after).toLocaleString() }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── TRANSFERS TAB ── -->
            <div v-show="entityModal.landTab === 'transfers'">
              <div v-if="!entityModal.data.transfers || !entityModal.data.transfers.length" class="em-empty-tab">
                <i class="bx bx-transfer"></i><p>No ownership transfer records found</p>
              </div>
              <div v-else class="em-permits-list">
                <div v-for="t in entityModal.data.transfers" :key="t.id" class="em-permit-card em-permit-card--active">
                  <div style="width:100%;">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <div class="em-permit-name">{{ t.transfer_type || 'Ownership Transfer' }}</div>
                        <div class="em-permit-meta">
                          <span class="em-mono">Ref: {{ t.transfer_reference }}</span>
                          <span>{{ t.transfer_date }}</span>
                          <span>Filed by {{ t.transferred_by }}</span>
                        </div>
                      </div>
                      <span class="em-permit-badge bg-success">Completed</span>
                    </div>
                    <div class="em-transfer-row">
                      <div class="em-transfer-party">
                        <div class="em-transfer-label">FROM</div>
                        <div class="em-transfer-name">{{ t.previous_owner_names }}</div>
                        <div class="em-transfer-meta">{{ t.previous_owner_id }} · {{ t.previous_owner_phone || '—' }}</div>
                      </div>
                      <div class="em-transfer-arrow"><i class="bx bx-right-arrow-alt"></i></div>
                      <div class="em-transfer-party">
                        <div class="em-transfer-label" style="color:#34c38f;">TO</div>
                        <div class="em-transfer-name text-primary">{{ t.new_owner_names }}</div>
                        <div class="em-transfer-meta">{{ t.new_owner_id }} · {{ t.new_owner_phone || '—' }}</div>
                      </div>
                    </div>
                    <div v-if="t.transfer_notes" class="em-transfer-notes">
                      <i class="bx bx-note me-1"></i>{{ t.transfer_notes }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── HISTORY TAB ── -->
            <div v-show="entityModal.landTab === 'history'">
              <div v-if="(!entityModal.data.useChanges || !entityModal.data.useChanges.length)" class="em-empty-tab">
                <i class="bx bx-history"></i><p>No change history found</p>
              </div>
              <div v-else>
                <div class="em-section-title mb-2"><i class="bx bx-building me-2"></i>Land Use Changes</div>
                <div class="em-permits-list">
                  <div v-for="u in entityModal.data.useChanges" :key="u.id" class="em-permit-card em-permit-card--active">
                    <div class="em-permit-left">
                      <div class="em-permit-icon"><i class="bx bx-building"></i></div>
                      <div>
                        <div class="em-permit-name">
                          <span class="text-muted">{{ u.old_land_use || 'Unknown' }}</span>
                          <i class="bx bx-right-arrow-alt mx-1 text-primary"></i>
                          <strong>{{ u.new_land_use_name }}</strong>
                        </div>
                        <div class="em-permit-meta">
                          <span class="em-mono">{{ u.bill_no }}</span>
                          <span>{{ u.changed_by }}</span>
                        </div>
                        <small class="text-muted">{{ formatDate(u.change_date) }}</small>
                      </div>
                    </div>
                    <div class="em-permit-right">
                      <small class="text-muted text-end" style="max-width:160px;word-break:break-word;">{{ u.change_reason }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <!-- ── RENTAL UNIT BODY ── -->
          <div v-else-if="entityModal.type === 'RENTAL_UNIT' && entityModal.data" class="em-body">
            <div class="em-stats-row">
              <div class="em-stat"><div class="em-stat-val em-mono text-primary">{{ entityModal.data.unitNo }}</div><div class="em-stat-lbl">Unit No</div></div>
              <div class="em-stat"><div class="em-stat-val text-success">Ksh {{ Number(entityModal.data.monthlyRent || 0).toLocaleString() }}</div><div class="em-stat-lbl">Monthly Rent</div></div>
              <div class="em-stat"><div class="em-stat-val text-danger">Ksh {{ Number(entityModal.data.arrears || 0).toLocaleString() }}</div><div class="em-stat-lbl">Arrears</div></div>
              <div class="em-stat">
                <div class="em-stat-val"><span class="em-status-pill" :class="entityModal.data.status === 'OCCUPIED' ? 'em-pill--green' : 'em-pill--grey'">{{ entityModal.data.status }}</span></div>
                <div class="em-stat-lbl">Status</div>
              </div>
            </div>
            <div class="em-section">
              <div class="em-section-title"><i class="bx bx-building-house me-2"></i>Unit Details</div>
              <div class="em-fields">
                <div class="em-field em-field--wide"><div class="em-field-lbl">Property</div><div class="em-field-val em-field-val--strong">{{ entityModal.data.property || '—' }}</div></div>
                <div class="em-field"><div class="em-field-lbl">Category</div><div class="em-field-val">{{ entityModal.data.category || '—' }}</div></div>
                <div class="em-field"><div class="em-field-lbl">Occupant</div><div class="em-field-val em-field-val--strong">{{ entityModal.data.occupants || '—' }}</div></div>
                <div class="em-field"><div class="em-field-lbl">Phone</div><div class="em-field-val em-mono text-primary">{{ entityModal.data.phoneNumber || '—' }}</div></div>
                <div class="em-field"><div class="em-field-lbl">ID No</div><div class="em-field-val em-mono">{{ entityModal.data.idNo || '—' }}</div></div>
                <div class="em-field"><div class="em-field-lbl">Current Balance</div><div class="em-field-val text-danger">Ksh {{ Number(entityModal.data.current_balance || 0).toLocaleString() }}</div></div>
                <div class="em-field"><div class="em-field-lbl">Sub-County</div><div class="em-field-val">{{ entityModal.data.subCountyName || '—' }}</div></div>
                <div class="em-field"><div class="em-field-lbl">Last Payment</div><div class="em-field-val">{{ entityModal.data.lastPaymentDate || '—' }}</div></div>
              </div>
            </div>
          </div>

          <!-- ── PLOT BODY ── -->
          <div v-else-if="entityModal.type === 'PLOT' && entityModal.data" class="em-body">
            <div class="em-stats-row">
              <div class="em-stat"><div class="em-stat-val em-mono text-primary">{{ entityModal.data.plotNumber }}</div><div class="em-stat-lbl">Plot Number</div></div>
              <div class="em-stat"><div class="em-stat-val text-success">Ksh {{ Number(entityModal.data.annualRent || 0).toLocaleString() }}</div><div class="em-stat-lbl">Annual Rent</div></div>
              <div class="em-stat"><div class="em-stat-val text-danger">Ksh {{ Number(entityModal.data.currentBalance || 0).toLocaleString() }}</div><div class="em-stat-lbl">Balance</div></div>
              <div class="em-stat"><div class="em-stat-val"><span class="em-status-pill" :class="entityModal.data.status === 'Active' ? 'em-pill--green' : 'em-pill--grey'">{{ entityModal.data.status }}</span></div><div class="em-stat-lbl">Status</div></div>
            </div>
            <div class="em-section">
              <div class="em-section-title"><i class="bx bx-user me-2"></i>Owner</div>
              <div class="em-fields">
                <div class="em-field em-field--wide"><div class="em-field-lbl">Owner</div><div class="em-field-val em-field-val--strong">{{ entityModal.data.ownerNames }}</div></div>
                <div class="em-field"><div class="em-field-lbl">Phone</div><div class="em-field-val em-mono text-primary">{{ entityModal.data.ownerPhoneNo || '—' }}</div></div>
                <div class="em-field"><div class="em-field-lbl">Email</div><div class="em-field-val">{{ entityModal.data.ownerEmail || '—' }}</div></div>
                <div class="em-field"><div class="em-field-lbl">Address</div><div class="em-field-val">{{ entityModal.data.plotAddress || '—' }}</div></div>
                <div class="em-field"><div class="em-field-lbl">Sub-County</div><div class="em-field-val">{{ entityModal.data.subCountyName || '—' }}</div></div>
                <div class="em-field"><div class="em-field-lbl">Last Payment</div><div class="em-field-val">{{ entityModal.data.lastPaymentDate || '—' }}</div></div>
              </div>
            </div>
          </div>

          <!-- ── CONSTRUCTION PERMIT BODY ── -->
          <div v-else-if="entityModal.type === 'CONSTRUCTION' && entityModal.data" class="em-body">

            <!-- Permit number hero if approved -->
            <div v-if="entityModal.data.permit_number" class="em-vehicle-plate" style="font-size:1.3rem;letter-spacing:3px;">
              {{ entityModal.data.permit_number }}
              <div style="font-size:0.7rem;font-weight:400;letter-spacing:1px;color:#666;margin-top:4px;">Permit Number</div>
            </div>

            <!-- Stats -->
            <div class="em-stats-row" style="grid-template-columns:repeat(4,1fr);">
              <div class="em-stat">
                <div class="em-stat-val">
                  <span class="em-status-pill"
                        :class="{ 'em-pill--green': entityModal.data.status === 'approved', 'em-pill--orange': entityModal.data.status === 'in_review' || entityModal.data.status === 'pending', 'em-pill--red': entityModal.data.status === 'rejected' }">
                    {{ (entityModal.data.status || '—').replace('_', ' ').toUpperCase() }}
                  </span>
                </div>
                <div class="em-stat-lbl">Status</div>
              </div>
              <div class="em-stat"><div class="em-stat-val" style="font-size:0.8rem;">{{ entityModal.data.development_type || '—' }}</div><div class="em-stat-lbl">Dev. Type</div></div>
              <div class="em-stat"><div class="em-stat-val text-success fw-bold">Ksh {{ Number(entityModal.data.project_value || 0).toLocaleString() }}</div><div class="em-stat-lbl">Project Value</div></div>
              <div class="em-stat"><div class="em-stat-val em-mono" style="font-size:0.78rem;">{{ entityModal.data.application_number }}</div><div class="em-stat-lbl">App. Number</div></div>
            </div>

            <!-- Sub-tabs -->
            <div class="em-subtabs mb-3">
              <button class="em-subtab" :class="{'em-subtab--active': entityModal.constructionTab === 'overview'}" @click="entityModal.constructionTab = 'overview'">
                <i class="bx bx-info-circle me-1"></i>Overview
              </button>
              <button class="em-subtab" :class="{'em-subtab--active': entityModal.constructionTab === 'workflow'}" @click="entityModal.constructionTab = 'workflow'">
                <i class="bx bx-git-branch me-1"></i>Workflow
                <span v-if="entityModal.data.workflow && entityModal.data.workflow.length" class="em-tab-badge ms-1">{{ entityModal.data.workflow.length }}</span>
              </button>
              <button class="em-subtab" :class="{'em-subtab--active': entityModal.constructionTab === 'reviews'}" @click="entityModal.constructionTab = 'reviews'">
                <i class="bx bx-check-shield me-1"></i>Reviews
                <span v-if="entityModal.data.reviews && entityModal.data.reviews.length" class="em-tab-badge ms-1">{{ entityModal.data.reviews.length }}</span>
              </button>
              <button class="em-subtab" :class="{'em-subtab--active': entityModal.constructionTab === 'payments'}" @click="entityModal.constructionTab = 'payments'">
                <i class="bx bx-money me-1"></i>Payments
              </button>
            </div>

            <!-- ── OVERVIEW TAB ── -->
            <div v-show="entityModal.constructionTab === 'overview'">
              <div class="em-section">
                <div class="em-section-title"><i class="bx bx-buildings me-2"></i>Project Details</div>
                <div class="em-fields">
                  <div class="em-field em-field--wide"><div class="em-field-lbl">Project Title</div><div class="em-field-val em-field-val--strong">{{ entityModal.data.project_title || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Development Type</div><div class="em-field-val">{{ entityModal.data.development_type || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Plan Type</div><div class="em-field-val">{{ entityModal.data.plan_type || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Sub-County</div><div class="em-field-val">{{ entityModal.data.subCountyName || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Ward</div><div class="em-field-val">{{ entityModal.data.wardName || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Plot Number</div><div class="em-field-val em-mono">{{ entityModal.data.plot_number || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">UPN</div><div class="em-field-val em-mono">{{ entityModal.data.upn_number || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Plot Size</div><div class="em-field-val">{{ entityModal.data.plot_size || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Land Use</div><div class="em-field-val">{{ entityModal.data.land_use || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Land Use Category</div><div class="em-field-val">{{ entityModal.data.land_use_category || '—' }}</div></div>
                  <div class="em-field em-field--wide" v-if="entityModal.data.project_description"><div class="em-field-lbl">Description</div><div class="em-field-val">{{ entityModal.data.project_description }}</div></div>
                  <div class="em-field em-field--wide" v-if="entityModal.data.location_description"><div class="em-field-lbl">Location Description</div><div class="em-field-val">{{ entityModal.data.location_description }}</div></div>
                </div>
              </div>

              <div class="em-section">
                <div class="em-section-title"><i class="bx bx-user me-2"></i>Owner / Applicant</div>
                <div class="em-fields">
                  <div class="em-field em-field--wide"><div class="em-field-lbl">Full Names</div><div class="em-field-val em-field-val--strong">{{ entityModal.data.owner_full_names || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Phone</div><div class="em-field-val em-mono text-primary">{{ entityModal.data.owner_phone_number || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Email</div><div class="em-field-val">{{ entityModal.data.owner_email || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">ID Number</div><div class="em-field-val em-mono">{{ entityModal.data.owner_id_number || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">KRA PIN</div><div class="em-field-val em-mono">{{ entityModal.data.owner_kra_pin || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Address</div><div class="em-field-val">{{ entityModal.data.owner_address || '—' }}</div></div>
                </div>
              </div>

              <!-- Professional -->
              <div class="em-section" v-if="entityModal.data.professional_name">
                <div class="em-section-title"><i class="bx bx-hard-hat me-2"></i>Professional / Firm</div>
                <div class="em-fields">
                  <div class="em-field em-field--wide"><div class="em-field-lbl">Professional</div><div class="em-field-val em-field-val--strong">{{ entityModal.data.professional_name }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Type</div><div class="em-field-val text-capitalize">{{ entityModal.data.professional_type || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Certificate No.</div><div class="em-field-val em-mono">{{ entityModal.data.professional_cert || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Cert. Expiry</div><div class="em-field-val" :class="isExpired(entityModal.data.professional_cert_expiry) ? 'text-danger' : 'text-success'">{{ entityModal.data.professional_cert_expiry || '—' }}<span v-if="isExpired(entityModal.data.professional_cert_expiry)"> ⚠</span></div></div>
                  <div class="em-field"><div class="em-field-lbl">Phone</div><div class="em-field-val em-mono">{{ entityModal.data.professional_phone || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Email</div><div class="em-field-val">{{ entityModal.data.professional_email || '—' }}</div></div>
                  <template v-if="entityModal.data.firm_name">
                    <div class="em-field em-field--wide"><div class="em-field-lbl">Firm</div><div class="em-field-val em-field-val--strong">{{ entityModal.data.firm_name }}</div></div>
                    <div class="em-field"><div class="em-field-lbl">Firm Reg. No.</div><div class="em-field-val em-mono">{{ entityModal.data.firm_reg || '—' }}</div></div>
                    <div class="em-field"><div class="em-field-lbl">Firm Phone</div><div class="em-field-val em-mono">{{ entityModal.data.firm_phone || '—' }}</div></div>
                    <div class="em-field"><div class="em-field-lbl">Firm Address</div><div class="em-field-val">{{ entityModal.data.firm_address || '—' }}</div></div>
                  </template>
                </div>
              </div>

              <!-- Land Details -->
              <div class="em-section" v-if="entityModal.data.landDetails">
                <div class="em-section-title"><i class="bx bx-map-alt me-2"></i>Land Details</div>
                <div class="em-fields">
                  <div class="em-field"><div class="em-field-lbl">Ownership Type</div><div class="em-field-val text-capitalize">{{ entityModal.data.landDetails.ownership_type || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Title Number</div><div class="em-field-val em-mono">{{ entityModal.data.landDetails.title_number || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Land Reference</div><div class="em-field-val em-mono">{{ entityModal.data.landDetails.land_reference || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Size (Acres)</div><div class="em-field-val">{{ entityModal.data.landDetails.land_size_acres || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Size (Hectares)</div><div class="em-field-val">{{ entityModal.data.landDetails.land_size_hectares || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Reg. Section</div><div class="em-field-val">{{ entityModal.data.landDetails.registration_section || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Reg. Block</div><div class="em-field-val">{{ entityModal.data.landDetails.registration_block || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Land Search Date</div><div class="em-field-val">{{ entityModal.data.landDetails.land_search_date || '—' }}</div></div>
                  <div class="em-field em-field--wide" v-if="entityModal.data.landDetails.encumbrances"><div class="em-field-lbl">Encumbrances</div><div class="em-field-val text-danger">{{ entityModal.data.landDetails.encumbrances }}</div></div>
                </div>
              </div>

              <!-- Permit details if issued -->
              <div class="em-section" v-if="entityModal.data.permit_number">
                <div class="em-section-title"><i class="bx bx-badge-check me-2"></i>Permit Issued</div>
                <div class="em-fields">
                  <div class="em-field"><div class="em-field-lbl">Permit Number</div><div class="em-field-val em-mono fw-bold text-success">{{ entityModal.data.permit_number }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Issued Date</div><div class="em-field-val">{{ entityModal.data.permit_issued_date || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Expiry Date</div><div class="em-field-val" :class="isExpired(entityModal.data.permit_expiry_date) ? 'text-danger fw-bold' : 'text-success'">{{ entityModal.data.permit_expiry_date || '—' }}<span v-if="isExpired(entityModal.data.permit_expiry_date)"> ⚠ Expired</span></div></div>
                  <div class="em-field"><div class="em-field-lbl">Issued By</div><div class="em-field-val">{{ entityModal.data.issued_by || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Approved By</div><div class="em-field-val">{{ entityModal.data.approved_by || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Approval Date</div><div class="em-field-val">{{ entityModal.data.approval_date || '—' }}</div></div>
                </div>
              </div>

              <!-- Documents -->
              <div class="em-section" v-if="entityModal.data.documents && entityModal.data.documents.length">
                <div class="em-section-title"><i class="bx bx-file me-2"></i>Submitted Documents</div>
                <div class="em-fields">
                  <div v-for="d in entityModal.data.documents" :key="d.id" class="em-field">
                    <div class="em-field-lbl">{{ d.document_type }}</div>
                    <div class="em-field-val">
                      <span class="em-permit-badge"
                            :class="d.status === 'approved' ? 'bg-success' : d.status === 'rejected' ? 'bg-danger' : 'bg-warning'">
                        {{ d.status || 'Pending' }}
                      </span>
                      <small v-if="d.rejection_reason" class="text-danger ms-1">{{ d.rejection_reason }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <div class="em-section">
                <div class="em-section-title"><i class="bx bx-cog me-2"></i>Application Info</div>
                <div class="em-fields">
                  <div class="em-field"><div class="em-field-lbl">Submitted By</div><div class="em-field-val">{{ entityModal.data.created_by || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Submission Date</div><div class="em-field-val">{{ formatDate(entityModal.data.created_at) }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Initial Bill No.</div><div class="em-field-val em-mono">{{ entityModal.data.billNo || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Final Bill No.</div><div class="em-field-val em-mono">{{ entityModal.data.billNoFinal || '—' }}</div></div>
                  <div class="em-field"><div class="em-field-lbl">Payment Status</div><div class="em-field-val"><span class="em-permit-badge" :class="entityModal.data.paymentStatus === 'PAID' ? 'bg-success' : 'bg-warning'">{{ entityModal.data.paymentStatus }}</span></div></div>
                  <div class="em-field"><div class="em-field-lbl">Final Payment</div><div class="em-field-val"><span class="em-permit-badge" :class="entityModal.data.finalPayment === 'PAID' ? 'bg-success' : 'bg-warning'">{{ entityModal.data.finalPayment }}</span></div></div>
                </div>
              </div>
            </div>

            <!-- ── WORKFLOW TAB ── -->
            <div v-show="entityModal.constructionTab === 'workflow'">
              <div v-if="!entityModal.data.workflow || !entityModal.data.workflow.length" class="em-empty-tab">
                <i class="bx bx-git-branch"></i><p>No workflow stages found</p>
              </div>
              <div v-else class="em-permits-list">
                <div v-for="(w, idx) in entityModal.data.workflow" :key="w.id"
                     class="em-permit-card"
                     :class="{ 'em-permit-card--active': w.status === 'approved', 'em-permit-card--expired': w.status === 'rejected', 'em-permit-card--unpaid': w.status === 'pending' || w.status === 'in_progress' }">
                  <div class="em-permit-left">
                    <div class="em-permit-icon" :style="w.status === 'approved' ? 'background:#f0fff4;color:#34c38f;' : w.status === 'rejected' ? 'background:#fff0f0;color:#ff4757;' : 'background:#f0f4ff;color:#1c3461;'">
                      <i class="bx" :class="w.status === 'approved' ? 'bx-check-circle' : w.status === 'rejected' ? 'bx-x-circle' : 'bx-time-five'"></i>
                    </div>
                    <div>
                      <div class="em-permit-name text-capitalize">{{ w.stage ? w.stage.replace(/_/g,' ') : 'Stage ' + (idx+1) }}</div>
                      <div class="em-permit-meta">
                        <span v-if="w.assigned_to">{{ w.assigned_to }}</span>
                        <span v-if="w.action_date">{{ formatDate(w.action_date) }}</span>
                      </div>
                      <div v-if="w.comments" class="em-description mt-1" style="font-size:0.78rem;">{{ w.comments }}</div>
                      <div v-if="w.rejection_reason" class="text-danger mt-1" style="font-size:0.78rem;"><i class="bx bx-error-circle me-1"></i>{{ w.rejection_reason }}</div>
                      <div v-if="w.deferred_reason" class="text-warning mt-1" style="font-size:0.78rem;"><i class="bx bx-pause-circle me-1"></i>{{ w.deferred_reason }}</div>
                    </div>
                  </div>
                  <div class="em-permit-right">
                    <span class="em-permit-badge"
                          :class="w.status === 'approved' ? 'bg-success' : w.status === 'rejected' ? 'bg-danger' : w.status === 'in_progress' ? 'bg-warning' : 'bg-secondary text-white'">
                      {{ (w.status || '').replace('_', ' ').toUpperCase() }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── REVIEWS TAB ── -->
            <div v-show="entityModal.constructionTab === 'reviews'">
              <div v-if="!entityModal.data.reviews || !entityModal.data.reviews.length" class="em-empty-tab">
                <i class="bx bx-check-shield"></i><p>No review records found</p>
              </div>
              <div v-else class="em-permits-list">
                <div v-for="r in entityModal.data.reviews" :key="r.id"
                     class="em-permit-card"
                     :class="{ 'em-permit-card--active': r.status === 'approved', 'em-permit-card--expired': r.status === 'rejected', 'em-permit-card--unpaid': r.status === 'pending' || r.status === 'in_review' }">
                  <div class="em-permit-left">
                    <div class="em-permit-icon" :style="r.status === 'approved' ? 'background:#f0fff4;color:#34c38f;' : r.status === 'rejected' ? 'background:#fff0f0;color:#ff4757;' : 'background:#f0f4ff;color:#1c3461;'">
                      <i class="bx bx-check-shield"></i>
                    </div>
                    <div>
                      <div class="em-permit-name text-capitalize">{{ r.review_type ? r.review_type.replace(/_/g,' ') : '—' }}</div>
                      <div class="em-permit-meta">
                        <span v-if="r.reviewer_name">{{ r.reviewer_name }}</span>
                        <span v-if="r.review_date">{{ formatDate(r.review_date) }}</span>
                      </div>
                      <div v-if="r.comments" class="em-description mt-1" style="font-size:0.78rem;">{{ r.comments }}</div>
                      <div v-if="r.conditions" class="mt-1" style="font-size:0.78rem;color:#b7770d;"><i class="bx bx-info-circle me-1"></i>Conditions: {{ r.conditions }}</div>
                      <div v-if="r.rejection_reason" class="text-danger mt-1" style="font-size:0.78rem;"><i class="bx bx-error-circle me-1"></i>{{ r.rejection_reason }}</div>
                    </div>
                  </div>
                  <div class="em-permit-right">
                    <span class="em-permit-badge"
                          :class="r.status === 'approved' ? 'bg-success' : r.status === 'rejected' ? 'bg-danger' : r.status === 'in_review' ? 'bg-warning' : 'bg-secondary text-white'">
                      {{ (r.status || '').replace('_', ' ').toUpperCase() }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Inspections -->
              <div v-if="entityModal.data.inspections && entityModal.data.inspections.length" class="mt-4">
                <div class="em-section-title mb-2"><i class="bx bx-search-alt me-2"></i>Site Inspections</div>
                <div class="em-permits-list">
                  <div v-for="ins in entityModal.data.inspections" :key="ins.id"
                       class="em-permit-card"
                       :class="ins.status === 'completed' ? 'em-permit-card--active' : ins.status === 'cancelled' ? 'em-permit-card--expired' : 'em-permit-card--unpaid'">
                    <div class="em-permit-left">
                      <div class="em-permit-icon"><i class="bx bx-search-alt"></i></div>
                      <div>
                        <div class="em-permit-name text-capitalize">{{ ins.inspection_type || 'Inspection' }}</div>
                        <div class="em-permit-meta">
                          <span v-if="ins.inspector_name">{{ ins.inspector_name }}</span>
                          <span>Scheduled: {{ formatDate(ins.scheduled_date) }}</span>
                        </div>
                        <div v-if="ins.findings" class="em-description mt-1" style="font-size:0.78rem;">{{ ins.findings }}</div>
                        <div v-if="ins.recommendations" class="mt-1 text-primary" style="font-size:0.78rem;"><i class="bx bx-bulb me-1"></i>{{ ins.recommendations }}</div>
                      </div>
                    </div>
                    <div class="em-permit-right">
                      <span class="em-permit-badge"
                            :class="ins.status === 'completed' ? 'bg-success' : ins.status === 'cancelled' ? 'bg-danger' : 'bg-warning'">
                        {{ (ins.status || '').toUpperCase() }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── PAYMENTS TAB ── -->
            <div v-show="entityModal.constructionTab === 'payments'">
              <!-- Payment summary -->
              <div class="em-stats-row mb-3" style="grid-template-columns:repeat(3,1fr);">
                <div class="em-stat"><div class="em-stat-val text-success">Ksh {{ Number(entityModal.data.receiptAmount || 0).toLocaleString() }}</div><div class="em-stat-lbl">Initial Paid</div></div>
                <div class="em-stat"><div class="em-stat-val text-success">Ksh {{ Number(entityModal.data.receiptAmountFinal || 0).toLocaleString() }}</div><div class="em-stat-lbl">Final Paid</div></div>
                <div class="em-stat"><div class="em-stat-val text-danger">Ksh {{ Number(entityModal.data.billBalance || 0).toLocaleString() }}</div><div class="em-stat-lbl">Balance</div></div>
              </div>

              <div v-if="!entityModal.data.payments || !entityModal.data.payments.length" class="em-empty-tab">
                <i class="bx bx-money"></i><p>No payment records found</p>
              </div>
              <div v-else class="em-permits-list">
                <div v-for="pay in entityModal.data.payments" :key="pay.id"
                     class="em-permit-card"
                     :class="pay.status === 'completed' ? 'em-permit-card--active' : pay.status === 'failed' ? 'em-permit-card--expired' : 'em-permit-card--unpaid'">
                  <div class="em-permit-left">
                    <div class="em-permit-icon" :style="pay.payment_type === 'final' ? 'background:#f0fff4;color:#34c38f;' : 'background:#f0f4ff;color:#1c3461;'">
                      <i class="bx" :class="pay.payment_type === 'final' ? 'bx-badge-check' : 'bx-credit-card'"></i>
                    </div>
                    <div>
                      <div class="em-permit-name">{{ pay.payment_type === 'final' ? 'Final Payment' : 'Initial Payment' }}</div>
                      <div class="em-permit-meta">
                        <span class="em-mono">{{ pay.invoice_number }}</span>
                        <span v-if="pay.receipt_number" class="em-mono">{{ pay.receipt_number }}</span>
                        <span>{{ pay.payment_method }}</span>
                        <span v-if="pay.transaction_reference" class="em-mono">{{ pay.transaction_reference }}</span>
                      </div>
                      <small class="text-muted">{{ formatDate(pay.payment_date) }}</small>
                      <div v-if="pay.notes" class="text-muted mt-1" style="font-size:0.75rem;">{{ pay.notes }}</div>
                    </div>
                  </div>
                  <div class="em-permit-right">
                    <div class="em-permit-paid fw-bold">Ksh {{ Number(pay.amount).toLocaleString() }}</div>
                    <span class="em-permit-badge"
                          :class="pay.status === 'completed' ? 'bg-success' : pay.status === 'failed' ? 'bg-danger' : 'bg-warning'">
                      {{ (pay.status || '').toUpperCase() }}
                    </span>
                    <div class="em-permit-actions">
                      <button class="em-print-btn" @click="printBillOnly(pay.invoice_number)">
                        <i class="bx bx-printer me-1"></i>Bill
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
      <!-- ===== END ENTITY MODAL ===== -->
    </div>
  </div>
  </body>

</template>

<script>
/* eslint-disable */
import { biller, execute, getRight, trade } from "@/api";
import NavigationBar from "@/components/Navigation";
import moment from "moment";

export default {
  name: "ClientProfile",
  components: { NavigationBar },

  data() {
    return {
      // Clients table
      clients: [],
      tableLoading: false,
      tableSearch: '',
      currentPage: 1,
      perPage: 20,
      totalClients: 0,
      totalPages: 1,
      searchTimeout: null,

      // Profile
      profileLoading: false,
      profileData: null,

      // Entity modal
      entityModal: {
        show: false, loading: false, ref: '', type: '', data: null,
        activeTab: 'overview', vehicleTab: 'details', landTab: 'overview', constructionTab: 'overview',
      },
      businessTabs: [
        { key: 'overview', label: 'Overview',  icon: 'bx-info-circle' },
        { key: 'permits',  label: 'Permits',   icon: 'bx-badge-check' },
        { key: 'map',      label: 'Map',        icon: 'bx-map-alt' },
      ],
      activeStream: null,
      expandedStream: null,
      expandedBills: [],
      expandLoading: false,
    };
  },

  computed: {
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const cur   = this.currentPage;
      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else {
        pages.push(1);
        if (cur > 3) pages.push('...');
        for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) pages.push(i);
        if (cur < total - 2) pages.push('...');
        pages.push(total);
      }
      return pages;
    }
  },

  mounted() {
    this.menuAccess();
    this.loadClients(1);
  },

  methods: {

    gotTo(route) { this.$router.push(route); },

    // ---- Table ----

    onTableSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => { this.loadClients(1); }, 400);
    },

    clearSearch() { this.tableSearch = ''; this.loadClients(1); },

    async loadClients(page) {
      this.tableLoading = true;
      this.currentPage  = page;
      try {
        const data = new FormData();
        data.append("function", "getClients");
        data.append("page",    page);
        data.append("perPage", this.perPage);
        if (this.tableSearch) data.append("search", this.tableSearch);

        const res = await execute(data, biller);
        if (res.data.success) {
          this.clients      = res.data.data.customers  || [];
          this.totalClients = res.data.data.total      || 0;
          this.totalPages   = res.data.data.totalPages || 1;
        }
      } catch (e) {
        console.error("Load clients error:", e);
      } finally {
        this.tableLoading = false;
      }
    },

    // ---- Profile ----

    async openProfile(client) {
      this.profileData    = { customer: client, summary: {}, streams: [], receipts: [] };
      this.profileLoading = true;
      this.expandedStream = null;
      this.expandedBills  = [];

      try {
        const data = new FormData();
        data.append("function",    "getClientProfile");
        data.append("phoneNumber", client.phoneNumber);

        const res = await execute(data, biller);
        if (res.data.success) {
          this.profileData = res.data.data;
        } else {
          this.profileData = null;
          alert(res.data.message || 'Could not load profile');
        }
      } catch (e) {
        console.error("Profile error:", e);
        this.profileData = null;
      } finally {
        this.profileLoading = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },

    backToList() {
      this.profileData    = null;
      this.expandedStream = null;
      this.expandedBills  = [];
    },

    // ---- Stream Expand ----

    async expandStream(stream) {
      if (this.expandedStream && this.expandedStream.incomeTypePrefix === stream.incomeTypePrefix) {
        this.expandedStream = null; this.expandedBills = []; this.activeStream = null; return;
      }
      this.expandedStream = stream;
      this.activeStream   = stream.incomeTypePrefix;
      this.expandLoading  = true;
      this.expandedBills  = stream.recentBills || [];

      try {
        const data = new FormData();
        data.append("function",         "getClientBillsByStream");
        data.append("phoneNumber",      this.profileData.customer.phoneNumber);
        data.append("incomeTypePrefix", stream.incomeTypePrefix);

        const res = await execute(data, biller);
        if (res.data.success) this.expandedBills = res.data.data.bills || [];
      } catch (e) {
        console.error("Stream bills error:", e);
      } finally {
        this.expandLoading = false;
        this.$nextTick(() => {
          const el = document.querySelector('.border-top.border-primary');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    },

    // ---- Formatting ----

    formatPrice(value) {
      const num = parseFloat(value) || 0;
      return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 2 }).format(num);
    },

    formatPriceShort(value) {
      const num = parseFloat(value) || 0;
      if (Math.abs(num) >= 1_000_000) return 'KES ' + (num / 1_000_000).toFixed(1) + 'M';
      if (Math.abs(num) >= 1_000)     return 'KES ' + (num / 1_000).toFixed(1) + 'K';
      return 'KES ' + num.toFixed(0);
    },

    formatDate(d) { return d ? moment(d).format('DD MMM YYYY') : '—'; },
    formatDateTime(d) { return d ? moment(d).format('DD/MM/YYYY HH:mm') : '—'; },
    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
    },

    // ---- Style ----

    getClearanceClass(p) { return p >= 80 ? 'text-success' : p >= 50 ? 'text-warning' : 'text-danger'; },
    getClearanceBarClass(p) { return p >= 80 ? 'bg-success' : p >= 50 ? 'bg-warning' : 'bg-danger'; },

    getBillStatusBadge(s) {
      return { 'PAID': 'bg-success', 'PARTIAL': 'bg-warning text-dark', 'UNPAID': 'bg-danger', 'CANCELLED': 'bg-secondary' }[(s||'').toUpperCase()] || 'bg-secondary';
    },

    getPaymentModeBadge(m) {
      return { 'MPESA': 'bg-success', 'CASH': 'bg-primary', 'BANK': 'bg-info', 'CHEQUE': 'bg-secondary' }[(m||'').toUpperCase()] || 'bg-light text-dark';
    },

    getStreamStatusBadge(stream) {
      const bal = parseFloat(stream.totalBalance) || 0;
      if (bal <= 0) return 'bg-success';
      if (parseFloat(stream.totalPaid) > 0) return 'bg-warning text-dark';
      return 'bg-danger';
    },

    getStreamStatusLabel(stream) {
      const bal = parseFloat(stream.totalBalance) || 0;
      if (bal <= 0) return 'Cleared';
      if (parseFloat(stream.totalPaid) > 0) return 'Partial';
      return 'Unpaid';
    },

    getStreamIcon(prefix) {
      const map = {
        'SBP':'bx-store','PLTRENT':'bx-home-alt','MARKET':'bx-shopping-bag','LIQUOR':'bx-beer',
        'LIBRARY':'bx-book-open','PARKING':'bx-car','HEALTH':'bx-plus-medical','REFUSE':'bx-trash',
        'WEIGHT':'bx-ruler','BURIAL':'bx-leaf','VET':'bx-heart','CROP':'bx-leaf',
        'INCOME':'bx-building','LIVEMVT':'bx-transfer','BUSPARK':'bx-bus',
        'APPLICATION':'bx-file','GAMEPARK':'bx-tree','BUILDING':'bx-buildings',
      };
      for (const key of Object.keys(map)) if ((prefix||'').toUpperCase().includes(key)) return map[key];
      return 'bx-receipt';
    },

    getStreamColor(prefix) {
      const colors = [
        'linear-gradient(135deg,#34c38f,#20956b)',  // green — primary accent
        'linear-gradient(135deg,#4a90d9,#2870b5)',  // blue — primary button color
        'linear-gradient(135deg,#50a5f1,#2980b9)',  // sky blue
        'linear-gradient(135deg,#f1b44c,#d4920a)',  // gold/amber
        'linear-gradient(135deg,#1abc9c,#0e8c71)',  // teal
        'linear-gradient(135deg,#3b82f6,#1c5ec9)',  // bright blue
        'linear-gradient(135deg,#2ecc71,#1a8c4d)',  // emerald
        'linear-gradient(135deg,#e67e22,#c0651b)',  // orange
        'linear-gradient(135deg,#1c3461,#2a4a8a)',  // navy
        'linear-gradient(135deg,#16a085,#0e6b5a)',  // dark teal
      ];
      let hash = 0;
      for (let i = 0; i < (prefix||'').length; i++) hash += prefix.charCodeAt(i);
      return colors[hash % colors.length];
    },

    isLinkedRef(ref) {
      if (!ref) return false;
      return /Business\s+ID[:]\s*\d+/i.test(ref) ||
          /^[A-Z]{3}\d{3,4}[A-Z]?$/i.test(ref.trim()) || // number plate pattern
          /^\d+$/.test(ref.trim()); // raw numeric ID
    },

    async openEntityModal(ref, prefix) {
      this.entityModal = { show: true, loading: true, ref, type: '', data: null, activeTab: 'overview', vehicleTab: 'details', landTab: 'overview', constructionTab: 'overview' };
      document.body.style.overflow = 'hidden';

      const p = (prefix || '').toUpperCase();
      let type = '';
      if (p.includes('SBP') || p.includes('FIRE') || p.includes('LIQUOR') || p.includes('HYGIENE') || p.includes('MEDICAL')) type = 'BUSINESS';
      else if (p.includes('PKN') || p.includes('PARK')) type = 'VEHICLE';
      else if (p.includes('LANDRATE') || p.includes('LAND')) type = 'LAND_PROPERTY';
      else if (p.includes('HSRENT') || p.includes('RENT')) type = 'RENTAL_UNIT';
      else if (p.includes('PLTRENT') || p.includes('PLOT')) type = 'PLOT';
      else if (p.includes('PLAN') || p.includes('ECON') || p.includes('CONSTRUCT')) type = 'CONSTRUCTION';

      this.entityModal.type = type;

      try {
        const data = new FormData();
        data.append("function",        "getEntityDetails");
        data.append("ref",             ref);
        data.append("incomeTypePrefix", prefix);

        const res = await execute(data, biller);
        if (res.data.success) {
          this.entityModal.data    = res.data.data.entity;
          this.entityModal.loading = false;
          // Init map if business with coordinates
          if (
              this.entityModal.type === 'BUSINESS' &&
              this.entityModal.data?.lat &&
              this.entityModal.data?.lng &&
              this.entityModal.data.lat != '0'
          ) {
            this.initBizMap(
                parseFloat(this.entityModal.data.lat),
                parseFloat(this.entityModal.data.lng),
                this.entityModal.data.businessName
            );
          }
        } else {
          this.entityModal.data    = null;
          this.entityModal.loading = false;
        }
      } catch (e) {
        console.error("Entity modal error:", e);
        this.entityModal.data    = null;
        this.entityModal.loading = false;
      }
    },

    closeEntityModal() {
      this.entityModal.show = false;
      document.body.style.overflow = '';
      if (this._bizMapInstance) {
        this._bizMapInstance.remove();
        this._bizMapInstance = null;
      }
    },

    // Print permit by billNo (same as printTradePermitBillNo in Approved.vue)
    printPermitByBillNo(billNo) {
      const data = new FormData();
      data.append("function", "printTradePermit");
      data.append("billNo", billNo);
      execute(data, trade).then(res => {
        if (res.data.success) {
          localStorage['params'] = JSON.stringify({ billNo });
          const routeData = this.$router.resolve({ name: 'trade' });
          window.open(routeData.href, '_blank');
        } else {
          alert('Could not load permit. Please try again.');
        }
      }).catch(err => console.error("Print permit error:", err));
    },

    // Print latest permit by businessID (same as printTradePermit in Approved.vue)
    printLatestPermit(businessID) {
      const data = new FormData();
      data.append("function", "printTradePermit");
      data.append("businessID", businessID);
      execute(data, trade).then(res => {
        if (res.data.success) {
          localStorage['params'] = JSON.stringify({ businessID });
          const routeData = this.$router.resolve({ name: 'trade' });
          window.open(routeData.href, '_blank');
        } else {
          alert('No approved permit found for this business.');
        }
      }).catch(err => console.error("Print latest permit error:", err));
    },

    // Print bill only
    printBillOnly(billNo) {
      localStorage['params'] = JSON.stringify({ billNo });
      const routeData = this.$router.resolve({ name: 'bill' });
      window.open(routeData.href, '_blank');
    },

    getParkingEntryLabel(e) {
      if (!e.endDate) return e.status || '—';
      if (new Date(e.endDate) < new Date()) return 'Expired';
      if (parseFloat(e.billBalance) > 0) return 'Unpaid';
      return 'Active';
    },
    getParkingEntryBadge(e) {
      const l = this.getParkingEntryLabel(e);
      if (l === 'Active')  return 'bg-success';
      if (l === 'Expired') return 'bg-danger';
      if (l === 'Unpaid')  return 'bg-warning';
      return 'bg-secondary';
    },
    getParkingEntryCardClass(e) {
      const l = this.getParkingEntryLabel(e);
      if (l === 'Active')  return 'em-permit-card--active';
      if (l === 'Expired') return 'em-permit-card--expired';
      return 'em-permit-card--unpaid';
    },

    getPermitCardClass(p) {
      const label = this.getPermitStatusLabel(p);
      if (label === 'Active')  return 'em-permit-card--active';
      if (label === 'Expired') return 'em-permit-card--expired';
      return 'em-permit-card--unpaid';
    },

    isExpired(dateStr) {
      if (!dateStr) return false;
      return new Date(dateStr) < new Date();
    },

    getPermitStatusLabel(p) {
      if (!p.endDate) return p.status || '—';
      if (new Date(p.endDate) < new Date()) return 'Expired';
      if (parseFloat(p.billBalance) > 0) return 'Unpaid';
      return 'Active';
    },

    getPermitStatusBadge(p) {
      const label = this.getPermitStatusLabel(p);
      if (label === 'Active')   return 'bg-success';
      if (label === 'Expired')  return 'bg-danger';
      if (label === 'Unpaid')   return 'bg-warning text-dark';
      return 'bg-secondary';
    },

    initBizMap(lat, lng, name) {
      this.$nextTick(() => {
        // Load Leaflet dynamically if not already loaded
        const loadLeaflet = (cb) => {
          if (window.L) { cb(); return; }
          const s = document.createElement('script');
          s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          s.onload = cb;
          document.head.appendChild(s);
        };

        loadLeaflet(() => {
          const el = this.$refs.bizMap;
          if (!el) return;

          // Destroy previous map instance if exists
          if (this._bizMapInstance) {
            this._bizMapInstance.remove();
            this._bizMapInstance = null;
          }

          const map = window.L.map(el, { zoomControl: true }).setView([lat, lng], 16);
          window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© <a href="https://www.openstreetmap.org/">OpenStreetMap</a>',
            maxZoom: 19
          }).addTo(map);

          window.L.marker([lat, lng])
              .addTo(map)
              .bindPopup(`<b>${name}</b>`)
              .openPopup();

          this._bizMapInstance = map;
        });
      });
    },

    getAccountLabel(prefix) {
      const p = (prefix || '').toUpperCase();
      if (p.includes('SBP') || p.includes('FIRE') || p.includes('LIQUOR') || p.includes('HYGIENE') || p.includes('MEDICAL')) return 'Business ID';
      if (p.includes('PKN') || p.includes('PARK')) return 'Number Plate';
      if (p.includes('LANDRATE') || p.includes('LAND')) return 'Property ID';
      if (p.includes('HSRENT') || p.includes('RENT')) return 'Unit No';
      if (p.includes('PLTRENT') || p.includes('PLOT')) return 'Plot ID';
      if (p.includes('PLAN') || p.includes('ECON') || p.includes('CONSTRUCT')) return 'Bill No';
      return 'Account / Permit Ref';
    },

    getEntityIcon(type) {
      const map = {
        'BUSINESS':      'bx-store',
        'VEHICLE':       'bx-car',
        'LAND_PROPERTY': 'bx-map-alt',
        'RENTAL_UNIT':   'bx-building-house',
        'PLOT':          'bx-home-alt',
        'CONSTRUCTION':  'bx-buildings',
      };
      return map[type] || 'bx-info-circle';
    },

    getEntityLabel(type) {
      const map = {
        'BUSINESS':      'Business Details',
        'VEHICLE':       'Vehicle Details',
        'LAND_PROPERTY': 'Land / Property Details',
        'RENTAL_UNIT':   'Rental Unit Details',
        'PLOT':          'Plot Details',
        'CONSTRUCTION':  'Construction Permit',
      };
      return map[type] || 'Linked Record';
    },

    isLinkedStream(prefix) {
      const p = (prefix || '').toUpperCase();
      return p.includes('SBP') || p.includes('PKN') || p.includes('PARK') ||
          p.includes('LANDRATE') || p.includes('HSRENT') || p.includes('RENT') ||
          p.includes('PLTRENT') || p.includes('FIRE') || p.includes('LIQUOR') ||
          p.includes('PLAN') || p.includes('ECON') || p.includes('CONSTRUCT');
    },

    menuAccess() {
      const data = new FormData();
      data.append("function",     "menuAccess");
      data.append("menu",         "Client Profile");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo",  sessionStorage.getItem("sessionIdNo"));
      execute(data, biller).catch(e => console.error("Menu access error:", e.message));
    }
  }
};
</script>

<style scoped>
.client-hero-card { background: linear-gradient(135deg, #1c3461 0%, #142850 100%); border-radius:1rem !important; }
.client-avatar {
  width:72px; height:72px; border-radius:50%; background:rgba(255,255,255,0.25);
  display:flex; align-items:center; justify-content:center;
  font-size:1.8rem; font-weight:700; color:#fff; border:3px solid rgba(255,255,255,0.5); flex-shrink:0;
}
.client-badge { background:rgba(255,255,255,0.2); color:#fff; padding:4px 12px; border-radius:20px; font-size:0.85rem; display:inline-flex; align-items:center; }

.mini-avatar {
  width:34px; height:34px; border-radius:50%;
  background:linear-gradient(135deg,#1c3461,#142850);
  display:flex; align-items:center; justify-content:center;
  font-size:0.75rem; font-weight:700; color:#fff; flex-shrink:0;
}
.clients-table tbody tr.client-row:hover td { background-color:#f0f7f4; }

.summary-card { transition:transform 0.2s ease; border-radius:0.75rem !important; }
.summary-card:hover { transform:translateY(-3px); }
.summary-icon { width:52px; height:52px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:1.4rem; }

.stream-card { border-radius:0.75rem !important; transition:transform 0.2s ease, box-shadow 0.2s ease; }
.stream-card:hover { transform:translateY(-3px); box-shadow:0 8px 25px rgba(0,0,0,0.12) !important; }
.stream-card--active { border:2px solid #34c38f !important; }
.stream-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:1.2rem; color:#fff; flex-shrink:0; }
.account-ref-box { background:#f8f9fc; border-radius:8px; padding:10px 14px; border-left:3px solid #34c38f; }

.bg-soft-primary  { background:rgba(74,144,217,0.12); }
.bg-soft-success  { background:rgba(46,204,113,0.12); }
.bg-soft-warning  { background:rgba(243,156,18,0.12); }
.bg-soft-danger   { background:rgba(231,76,60,0.12); }
.bg-soft-info     { background:rgba(52,152,219,0.12); }
.bg-soft-secondary{ background:rgba(108,117,125,0.12); }

.table th { font-size:0.78rem; text-transform:uppercase; letter-spacing:0.5px; white-space:nowrap; }
.font-monospace { font-family:'Courier New',monospace; font-size:0.85rem; }
.card { border-radius:0.75rem; }
.bx-spin { animation:spin 1s linear infinite; }
@keyframes spin { 0%{transform:rotate(0deg);} 100%{transform:rotate(360deg);} }

.biz-map {
  height: 280px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e0e3f0;
  z-index: 1;
}

/* Linked ref clickable */
.linked-ref {
  cursor: pointer;
  text-decoration: underline dotted;
  transition: color 0.15s;
}
.linked-ref:hover { color: #34c38f !important; }

/* Entity Modal */
/* ═══════════════════════════════════════
   ENTITY MODAL — Modern Sheet Design
   ═══════════════════════════════════════ */
.em-overlay {
  position: fixed; inset: 0;
  background: rgba(15,15,30,0.6);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  animation: em-fadein 0.2s ease;
}
@keyframes em-fadein { from { opacity:0 } to { opacity:1 } }

.em-sheet {
  background: #fff;
  border-radius: 16px;
  width: 100%; max-width: 700px;
  max-height: 90vh;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 80px rgba(0,0,0,0.35);
  overflow: hidden;
  animation: em-slidein 0.25s cubic-bezier(0.34,1.56,0.64,1);
}
@keyframes em-slidein { from { transform: translateY(30px); opacity:0 } to { transform: translateY(0); opacity:1 } }

/* Header */
.em-header {
  position: relative;
  background: linear-gradient(135deg, #1c3461 0%, #142850 100%);
  flex-shrink: 0;
  overflow: hidden;
}
.em-header--business      { background: linear-gradient(160deg, #142850 0%, #1c3461 100%); border-bottom: 3px solid #34c38f; }
.em-header--vehicle       { background: linear-gradient(160deg, #142850 0%, #1c3461 100%); border-bottom: 3px solid #4a90d9; }
.em-header--land_property { background: linear-gradient(160deg, #142850 0%, #1c3461 100%); border-bottom: 3px solid #f1b44c; }
.em-header--rental_unit   { background: linear-gradient(160deg, #142850 0%, #1c3461 100%); border-bottom: 3px solid #50a5f1; }
.em-header--plot          { background: linear-gradient(160deg, #142850 0%, #1c3461 100%); border-bottom: 3px solid #34c38f; }
.em-header--construction  { background: linear-gradient(160deg, #142850 0%, #1c3461 100%); border-bottom: 3px solid #f1b44c; }

.em-header-bg {
  position: absolute; inset: 0;
  background: radial-gradient(ellipse at top right, rgba(255,255,255,0.08) 0%, transparent 60%);
}
.em-header-content {
  position: relative;
  display: flex; align-items: flex-start; gap: 14px;
  padding: 20px 20px 14px;
}
.em-header-icon {
  width: 48px; height: 48px; border-radius: 12px;
  background: rgba(255,255,255,0.15);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.5rem; color: #fff; flex-shrink: 0;
  border: 1px solid rgba(255,255,255,0.2);
}
.em-header-text { flex: 1; min-width: 0; }
.em-header-label { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1px; color: rgba(255,255,255,0.6); margin-bottom: 2px; }
.em-header-title { font-size: 1.15rem; font-weight: 700; color: #fff; line-height: 1.3; word-break: break-word; }
.em-header-meta { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 6px; }
.em-header-meta > span { font-size: 0.78rem; color: rgba(255,255,255,0.75); display: flex; align-items: center; }
.em-close {
  background: rgba(255,255,255,0.15); border: none; color: #fff;
  width: 32px; height: 32px; border-radius: 8px; font-size: 1.2rem;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; flex-shrink: 0; transition: background 0.15s;
}
.em-close:hover { background: rgba(255,255,255,0.25); }

/* Status pills in header */
.em-status-pill { border-radius: 20px; padding: 2px 10px; font-size: 0.72rem; font-weight: 700; }
.em-pill--green  { background: rgba(46,213,115,0.25); color: #2ed573; border: 1px solid rgba(46,213,115,0.4); }
.em-pill--orange { background: rgba(255,165,2,0.25);  color: #ffa502; border: 1px solid rgba(255,165,2,0.4); }
.em-pill--red    { background: rgba(255,71,87,0.25);  color: #ff4757; border: 1px solid rgba(255,71,87,0.4); }
.em-pill--grey   { background: rgba(255,255,255,0.15); color: rgba(255,255,255,0.7); }

/* Tabs */
.em-tabs { position: relative; display: flex; gap: 4px; padding: 0 16px; }
.em-tab {
  background: transparent; border: none; border-bottom: 3px solid transparent;
  color: rgba(255,255,255,0.55); padding: 10px 14px 8px;
  font-size: 0.82rem; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; gap: 4px;
  transition: color 0.15s, border-color 0.15s;
}
.em-tab:hover { color: rgba(255,255,255,0.85); }
.em-tab--active { color: #fff; border-bottom-color: #fff; }
.em-tab-badge {
  background: rgba(255,255,255,0.25); color: #fff;
  border-radius: 10px; padding: 1px 7px; font-size: 0.7rem;
}

/* Body */
.em-body { overflow-y: auto; flex: 1; padding: 20px; }

/* Stats row */
.em-stats-row {
  display: grid; grid-template-columns: repeat(4, 1fr);
  gap: 12px; margin-bottom: 20px;
}
@media (max-width: 500px) { .em-stats-row { grid-template-columns: repeat(2, 1fr); } }
.em-stat {
  background: #f8f9fc; border-radius: 12px; padding: 14px 12px; text-align: center;
  border: 1px solid #eef0f8;
}
.em-stat-val { font-size: 0.95rem; font-weight: 700; color: #2d3748; line-height: 1.2; word-break: break-word; }
.em-stat-lbl { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.5px; color: #999; margin-top: 4px; }

/* Sections */
.em-section { margin-bottom: 20px; }
.em-section-title {
  font-size: 0.7rem; font-weight: 800; text-transform: uppercase;
  letter-spacing: 1px; color: #34c38f;
  display: flex; align-items: center;
  border-bottom: 2px solid #f0f0f8; padding-bottom: 8px; margin-bottom: 12px;
}
.em-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
@media (max-width: 500px) { .em-fields { grid-template-columns: 1fr; } }
.em-field {
  background: #fafbfc; border-radius: 8px; padding: 10px 12px;
  border: 1px solid #eef0f5;
}
.em-field--wide { grid-column: 1 / -1; }
.em-field-lbl { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.5px; color: #aaa; margin-bottom: 3px; }
.em-field-val { font-size: 0.85rem; color: #2d3748; word-break: break-word; }
.em-field-val--strong { font-weight: 700; color: #1a202c; font-size: 0.9rem; }
.em-mono { font-family: 'Courier New', monospace !important; }

/* Description */
.em-description { font-size: 0.82rem; color: #666; line-height: 1.6; background: #f8f9fc; border-radius: 8px; padding: 12px; border-left: 3px solid #34c38f; }

/* Loading / empty */
.em-loading, .em-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 60px 20px; color: #999; flex: 1;
}
.em-spinner {
  width: 40px; height: 40px; border-radius: 50%;
  border: 3px solid #f0f0f8; border-top-color: #34c38f;
  animation: em-spin 0.8s linear infinite; margin-bottom: 12px;
}
@keyframes em-spin { to { transform: rotate(360deg); } }
.em-empty i, .em-loading i { font-size: 3rem; opacity: 0.25; margin-bottom: 10px; }
.em-empty-tab { display: flex; flex-direction: column; align-items: center; padding: 40px; color: #bbb; }
.em-empty-tab i { font-size: 2.5rem; opacity: 0.3; margin-bottom: 8px; }

/* Permit cards */
.em-permits-list { display: flex; flex-direction: column; gap: 10px; }
.em-permit-card {
  border-radius: 12px; padding: 14px 16px;
  display: flex; justify-content: space-between; align-items: flex-start; gap: 12px;
  border: 1px solid #eef0f8; background: #fafbfc;
  transition: box-shadow 0.15s;
}
.em-permit-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.08); }
.em-permit-card--active  { border-left: 4px solid #2ed573; }
.em-permit-card--expired { border-left: 4px solid #ff4757; background: #fff8f8; }
.em-permit-card--unpaid  { border-left: 4px solid #ffa502; background: #fffaf0; }
.em-permit-left { display: flex; gap: 12px; align-items: flex-start; flex: 1; }
.em-permit-icon {
  width: 36px; height: 36px; border-radius: 8px; flex-shrink: 0;
  background: #f0f0f8; display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem; color: #34c38f;
}
.em-permit-name { font-weight: 600; font-size: 0.88rem; color: #2d3748; }
.em-permit-meta { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
.em-permit-meta span { font-size: 0.72rem; color: #888; background: #f0f0f8; border-radius: 4px; padding: 1px 6px; }
.em-permit-dates { display: flex; align-items: center; gap: 6px; margin-top: 6px; font-size: 0.78rem; }
.em-permit-arrow { color: #ccc; }
.em-permit-right { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; flex-shrink: 0; }
.em-permit-amount { text-align: right; }
.em-permit-paid { font-size: 0.88rem; font-weight: 700; color: #2d3748; }
.em-permit-balance { font-size: 0.75rem; color: #ff4757; }
.em-permit-badge { border-radius: 20px; padding: 3px 12px; font-size: 0.72rem; font-weight: 700; }
.em-permit-badge.bg-success { background: rgba(46,213,115,0.15) !important; color: #0d9e55; border: 1px solid rgba(46,213,115,0.3); }
.em-permit-badge.bg-danger  { background: rgba(255,71,87,0.12)  !important; color: #c0392b; border: 1px solid rgba(255,71,87,0.25); }
.em-permit-badge.bg-warning { background: rgba(255,165,2,0.12)  !important; color: #b7770d; border: 1px solid rgba(255,165,2,0.25); }
.em-permit-badge.bg-secondary { background: rgba(108,117,125,0.12) !important; color: #555; border: 1px solid rgba(108,117,125,0.25); }

/* Vehicle plate display */
.em-vehicle-plate {
  font-family: 'Courier New', monospace; font-size: 2rem; font-weight: 900;
  text-align: center; letter-spacing: 6px;
  background: #f8f9fc; border-radius: 12px; padding: 16px;
  border: 3px solid #e0e3f0; color: #1a202c; margin-bottom: 20px;
}

/* Map */
.em-map-info { display: flex; align-items: center; padding: 10px; font-size: 0.82rem; margin-bottom: 8px; }
.em-map { height: 300px; border-radius: 12px; overflow: hidden; border: 1px solid #e0e3f0; }

/* Quick action buttons (business overview) */
.em-quick-actions { display: flex; gap: 8px; margin-bottom: 20px; flex-wrap: wrap; }
.em-action-btn {
  padding: 8px 16px; border-radius: 8px; font-size: 0.82rem; font-weight: 600;
  display: flex; align-items: center; cursor: pointer; border: none; transition: all 0.15s;
}
.em-action-btn--primary { background: #1c3461; color: #fff; }
.em-action-btn--primary:hover { background: #142850; box-shadow: 0 4px 12px rgba(28,52,97,0.35); }
.em-action-btn--outline { background: #fff; color: #1c3461; border: 1.5px solid #1c3461; }
.em-action-btn--outline:hover { background: #f0f4fa; }

/* Per-permit print buttons */
.em-permit-actions { display: flex; gap: 6px; margin-top: 6px; }
.em-print-btn {
  padding: 4px 10px; border-radius: 6px; font-size: 0.72rem; font-weight: 600;
  display: flex; align-items: center; cursor: pointer; border: none; transition: all 0.15s;
  background: #1c3461; color: #fff;
}
.em-print-btn:hover { background: #142850; }
.em-print-btn--bill { background: #f8f9fc; color: #555; border: 1px solid #dde0e8; }
.em-print-btn--bill:hover { background: #eef0f5; }

/* Alert banners */
.em-alert {
  display: flex; align-items: flex-start; flex-wrap: wrap; gap: 4px;
  padding: 10px 14px; border-radius: 8px; font-size: 0.82rem;
  margin-bottom: 12px; border: 1px solid transparent;
}
.em-alert--danger  { background: #fff0f0; color: #c0392b; border-color: #ffd5d5; }
.em-alert--warning { background: #fff9ed; color: #b7770d; border-color: #ffe8a1; }
.em-alert--info    { background: #f0f6ff; color: #1c5ec9; border-color: #c5d8ff; }

/* Transfer layout */
.em-transfer-row {
  display: flex; align-items: center; gap: 12px;
  background: #f8f9fc; border-radius: 10px; padding: 12px 14px;
  margin: 10px 0;
}
.em-transfer-party { flex: 1; min-width: 0; }
.em-transfer-label { font-size: 0.65rem; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; color: #999; margin-bottom: 2px; }
.em-transfer-name  { font-weight: 700; font-size: 0.88rem; color: #1a202c; }
.em-transfer-meta  { font-size: 0.75rem; color: #888; margin-top: 2px; }
.em-transfer-arrow { font-size: 1.6rem; color: #1c3461; flex-shrink: 0; }
.em-transfer-notes { font-size: 0.78rem; color: #666; background: #f0f4fa; border-radius: 6px; padding: 8px 12px; border-left: 3px solid #1c3461; }

/* Vehicle sub-tabs */
.em-subtabs { display: flex; gap: 6px; border-bottom: 2px solid #f0f0f8; }
.em-subtab {
  background: none; border: none; border-bottom: 3px solid transparent;
  margin-bottom: -2px; padding: 8px 14px;
  font-size: 0.82rem; font-weight: 600; color: #aaa; cursor: pointer;
  display: flex; align-items: center; transition: color 0.15s, border-color 0.15s;
}
.em-subtab:hover { color: #34c38f; }
.em-subtab--active { color: #34c38f; border-bottom-color: #34c38f; }

.entity-panel {
  background: #f8f9fc;
  border-radius: 8px;
  border: 1px solid #e8eaf0;
  overflow: hidden;
}
.entity-panel-header {
  background: #1c3461;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding: 6px 12px;
}
.entity-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 12px;
  border-bottom: 1px solid #eef0f5;
  font-size: 0.82rem;
  gap: 8px;
}
.entity-row:last-child { border-bottom: none; }
.entity-row > span:first-child {
  color: #888;
  white-space: nowrap;
  min-width: 80px;
  flex-shrink: 0;
}
.entity-row > *:last-child {
  text-align: right;
  word-break: break-word;
}
</style>